import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'fr';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

const translations = {
  fr: {
    nav: {
      home: 'Accueil',
      particuliers: 'Particuliers',
      professionnels: 'Professionnels',
      contact: 'Contact',
      contactUs: 'Nous Contacter'
    },
    home: {
      space: 'Espace',
      particuliers: 'Particuliers',
      professionnels: 'Professionnels',
      particuliersDesc: 'Conseils et accompagnement pour vos procédures de visa Schengen et Canada',
      professionnelsDesc: 'Stratégie commerciale, formation et conseil en hygiène industrielle',
      discover: 'Découvrir',
      about: 'À Propos',
      aboutTitle: "L'Excellence au Service de Votre Réussite",
      aboutText1: "Excelsium Conseils est votre partenaire de confiance pour tous vos projets d'immigration et de développement d'entreprise. Notre expertise reconnue et notre engagement envers l'excellence nous permettent de vous accompagner efficacement dans toutes vos démarches.",
      aboutText2: "Que vous soyez un particulier à la recherche d'un accompagnement personnalisé pour vos procédures de visa, ou une entreprise souhaitant optimiser ses performances, nous mettons notre savoir-faire à votre service.",
      yearsExp: "Années d'Expérience",
      whyUs: 'Pourquoi Nous Choisir',
      commitment: 'Notre Engagement Qualité',
      expertise: 'Expertise',
      expertiseDesc: 'Une équipe de consultants experts dans leurs domaines respectifs',
      support: 'Accompagnement',
      supportDesc: 'Un suivi personnalisé tout au long de vos démarches',
      reactivity: 'Réactivité',
      reactivityDesc: 'Des réponses rapides et un traitement efficace de vos dossiers',
      trust: 'Confiance',
      trustDesc: 'Transparence et confidentialité garanties dans tous nos services',
      ready: 'Prêt à Commencer?',
      together: 'Concrétisons Ensemble Vos Projets',
      ctaText: "Contactez-nous dès aujourd'hui pour une consultation et découvrez comment Excelsium Conseils peut vous accompagner vers la réussite.",
      ourServices: 'Nos Services',
      teamBanner: 'Une équipe multiculturelle et chevronnée, composée de talents français, canadiens et tunisiens, au service de votre réussite.',
      teamHighlight: 'Excelsium Conseils'
    },
    particuliers: {
      title: 'Nos Services aux Particuliers',
      subtitle: "Visa & Immigration, Gestion Immobilière et Maîtrise d'Œuvre",
      services: 'Nos Services',
      complete: 'Un Accompagnement Complet',
      visaSchengen: 'Visa Schengen',
      visaSchengenDesc: "Accompagnement pour vos demandes de visa vers les 27 pays de l'espace Schengen",
      visaCanada: 'Visa Canada',
      visaCanadaDesc: 'Touriste, étudiant, travail ou résidence permanente au Canada',
      gestionImmo: 'Gestion Immobilière',
      gestionImmoDesc: 'Gestion locative complète et suivi de votre patrimoine immobilier',
      maitriseOeuvre: "Maîtrise d'Œuvre",
      maitriseOeuvreDesc: 'Pilotage de vos projets de construction et rénovation',
      service: 'Service',
      successRate: 'Taux de Réussite',
      ourSuccessRate: 'Notre Taux de Réussite',
      successRateValue: '+75%',
      successRateLabel: 'de dossiers acceptés',
      vsCompetitors: 'Taux de refus moyen',
      competitorRate: '~40%',
      competitorRateLabel: 'chez les autres prestataires',
      successRateText: 'Grâce à notre expertise et notre accompagnement personnalisé, nous obtenons un taux de réussite nettement supérieur à la moyenne du marché. Là où de nombreux dossiers sont refusés faute de préparation adéquate, nous maximisons vos chances de succès.',
      gestionImmoTitle: 'Gestion Immobilière',
      gestionImmoText: 'Confiez-nous la gestion de vos biens immobiliers en toute sérénité. Notre équipe assure un suivi rigoureux et personnalisé de votre patrimoine.',
      gestionLocative: 'Gestion locative complète',
      rechercheLocataires: 'Recherche et sélection de locataires',
      suiviLoyers: 'Suivi des loyers et charges',
      entretien: 'Entretien et maintenance des biens',
      maitriseOeuvreTitle: "Maîtrise d'Œuvre",
      maitriseOeuvreText: 'Nous pilotons vos projets de construction et de rénovation de A à Z, en garantissant qualité, délais et budget.',
      conception: 'Conception et études techniques',
      coordination: 'Coordination des travaux',
      suiviChantier: 'Suivi de chantier',
      reception: 'Réception et contrôle qualité',
      schengenText: "L'espace Schengen regroupe 27 pays européens permettant la libre circulation. Notre service d'accompagnement visa Schengen comprend :",
      analyseConseil: 'Analyse de votre situation et conseil personnalisé',
      constitution: 'Constitution du dossier complet',
      verification: 'Vérification et optimisation des documents',
      preparation: "Obtention de votre rendez-vous avec les autorités compétentes pour l'obtention du visa",
      suivi: 'Suivi de votre demande',
      schengenCountries: "Pays de l'Espace Schengen",
      canadaText: "Le Canada offre de nombreuses opportunités d'immigration. Nous vous accompagnons pour les différents types de visa :",
      visaTouriste: 'Visa Touriste',
      visitCanada: 'Visite et découverte du Canada',
      visaEtudiant: 'Visa Étudiant',
      etudes: 'Études dans les universités canadiennes',
      workvisa: 'Permis de Travail',
      workOpportunities: 'Opportunités professionnelles',
      residence: 'Résidence Permanente',
      immigration: 'Immigration définitive au Canada',
      visaMedical: 'Visa Visite Médicale',
      medicalDesc: 'Soins médicaux et traitements au Canada',
      visaFamille: 'Visa Visite Famille / Amis',
      familleDesc: 'Rendre visite à vos proches résidant au Canada',
      process: 'Notre Processus',
      howWeWork: 'Comment Nous Travaillons',
      consultation: 'Consultation',
      consultationDesc: "Analyse de votre situation et de vos objectifs d'immigration",
      preparationStep: 'Préparation',
      preparationStepDesc: 'Constitution et vérification de votre dossier complet',
      submission: 'Soumission',
      submissionDesc: 'Dépôt de votre demande et suivi auprès des autorités',
      supportStep: 'Accompagnement',
      supportStepDesc: "Support continu jusqu'à votre rendez-vous avec les autorités consulaires ou leurs représentantes",
      readyToStart: 'Prêt à Lancer Votre Projet?',
      contactForConsultation: "Contactez-nous pour une consultation et découvrez comment nous pouvons vous aider à réaliser votre projet d'immigration.",
      requestConsultation: 'Demander une Consultation',
      visaContactTitle: 'Contact Visa',
      visaContactSubtitle: 'Contactez Notre Service Visa',
      visaPhone: 'WhatsApp Visa',
      whatsappOnly: 'Uniquement joignable par WhatsApp',
      visaEmail: 'Email Visa',
      visaEmailDesc: 'Pour toute demande relative aux visas',
      domotique: 'Conseil en Installation Domotique',
      domotiqueDesc: 'Optimisez votre confort et votre consommation énergétique grâce à nos conseils en domotique et bâtiments connectés.',
      domotiqueDetail1: 'Gestion technique du bâtiment (GTB)',
      domotiqueDetail2: 'Gestion technique centralisée (chauffage, climatisation, ventilation, éclairage)',
      domotiqueDetail3: 'Optimisation des installations (volets roulants, portails, éclairage, station météo)',
      domotiqueDetail4: 'Capteurs de présence et de mouvement, alarme et vidéosurveillance',
      domotiqueDetail5: 'Paramétrage d\'objets connectés',
      domotiqueDetail6: 'Conseil personnalisé pour votre projet de maison intelligente'
    },
    professionnels: {
      title: 'Conseils & Formations',
      conseils: 'Conseils',
      formationsLink: 'Formations',
      subtitle: 'Stratégie commerciale, formation des équipes et conseils en fibres et habilitation électrique et en hygiène industrielle',
      services: 'Nos Services',
      solutions: 'Solutions sur Mesure pour Votre Entreprise',
      strategie: 'Stratégie Commerciale',
      strategieDesc: 'Développez votre activité avec une stratégie commerciale sur mesure, adaptée à votre marché et vos objectifs.',
      analyseMarche: 'Analyse de marché et positionnement',
      objectifs: 'Définition des objectifs commerciaux',
      planAction: "Plan d'action et mise en œuvre",
      suiviKPI: 'Suivi des indicateurs de performance',
      optimisation: 'Optimisation continue de la stratégie',
      formation: 'Formation des Équipes',
      formationDesc: "Renforcez les compétences de vos équipes commerciales pour atteindre l'excellence.",
      techniquesVente: 'Techniques de vente avancées',
      negociation: 'Négociation commerciale',
      relationClient: 'Gestion de la relation client',
      leadership: 'Leadership commercial',
      coaching: 'Coaching individualisé',
      hygiene: 'Hygiène et Nettoyage Industriel',
      hygieneDesc: "Garantissez la conformité et la sécurité de vos processus industriels en matière d'hygiène et de nettoyage.",
      auditInstallations: 'Audit des installations',
      conformite: 'Mise en conformité réglementaire',
      plansSanitaires: 'Plans de maîtrise sanitaire',
      formationPersonnel: 'Formation du personnel',
      certification: 'Accompagnement certification',
      clientMystere: 'Client Mystère',
      clientMystereDesc: "Évaluez la qualité de votre service client et l'expérience d'achat grâce à nos visites mystères.",
      visitesMysteres: 'Visites mystères en point de vente',
      appelsMysteres: 'Appels et contacts mystères',
      evaluationAccueil: "Évaluation de l'accueil et du conseil",
      rapportRecommandations: 'Rapport détaillé avec recommandations',
      suiviAmelioration: "Suivi des plans d'amélioration",
      marketing: 'Marketing et Création Digitale',
      marketingDesc: 'Boostez votre présence en ligne et développez votre image de marque avec nos solutions digitales.',
      strategieDigital: 'Stratégie marketing digital',
      creationSites: 'Création de sites web et landing pages',
      reseauxSociaux: 'Gestion des réseaux sociaux',
      creationContenu: 'Création de contenu et design graphique',
      campagnesPub: 'Campagnes publicitaires en ligne',
      expertise: 'Notre Expertise',
      accelerate: 'Accélérez la Croissance de Votre Entreprise',
      expertiseText1: 'Excelsium Conseils accompagne les entreprises dans leur développement commercial et leur mise en conformité. Notre approche personnalisée et notre expertise sectorielle nous permettent de vous proposer des solutions adaptées à vos enjeux.',
      expertiseText2: "De la définition de votre stratégie commerciale à la formation de vos équipes, en passant par le conseil en hygiène industrielle, nous sommes votre partenaire de confiance pour atteindre l'excellence.",
      entreprises: 'Entreprises Accompagnées',
      satisfaction: 'de Satisfaction Client',
      secteurs: "Secteurs d'Activité",
      results: 'Nos Résultats',
      concreteResults: 'Des Résultats Concrets pour Votre Entreprise',
      resultsText: 'Notre approche orientée résultats nous permet de générer une valeur mesurable pour nos clients. Nous nous engageons sur des objectifs clairs et accompagnons leur réalisation.',
      methodology: 'Méthodologie éprouvée et adaptable',
      expertsConsultants: 'Consultants experts dans leur domaine',
      personalizedSupport: 'Accompagnement personnalisé et suivi continu',
      croissance: 'Croissance moyenne du CA',
      auditsReussis: 'Audits réussis',
      collaborateurs: 'Collaborateurs formés',
      recommandation: 'Taux de recommandation',
      multiSector: 'Une Expertise Multi-Sectorielle',
      readyToDevelop: 'Prêt à Développer Votre Activité?',
      contactForAnalysis: "Contactez-nous pour une analyse de vos besoins et découvrez comment Excelsium Conseils peut accompagner la croissance de votre entreprise.",
      requestQuote: 'Demander un Devis',
      seeDetails: 'Voir les offres'
    },
    cm: {
      tagline: 'Client Mystère',
      heroTitle: 'Le Client Mystère au Service de Votre Excellence',
      heroSubtitle: "Évaluez objectivement la qualité de votre accueil, de votre service client et de l'expérience d'achat grâce à nos visites mystères professionnelles.",
      simpleProcess: 'Simple et Efficace',
      howItWorks: 'Comment Ça Fonctionne',
      step1Title: 'Préparation',
      step1Desc: "Nous personnalisons ensemble les paramètres de vos visites mystères : calendrier, questionnaire d'évaluation, profil et scénario de vos clients mystères, adaptés à votre secteur d'activité.",
      step2Title: 'Visites Mystères',
      step2Desc: "Sur place et/ou par téléphone, nos clients mystères entrent en action. Ils se comportent comme n'importe lequel de vos clients habituels, évaluant chaque aspect de l'expérience.",
      step3Title: 'Résultats & Analyse',
      step3Desc: "Vous recevez un rapport détaillé après chaque visite mystère. Analysez vos résultats en ligne : tri, comparaison, filtrage et export des données pour un suivi optimal.",
      objectivesTag: 'Pourquoi le Client Mystère',
      objectivesTitle: 'Quatre Objectifs Stratégiques',
      obj1Title: 'Développer le Chiffre d\'Affaires',
      obj1Desc: "La croissance du chiffre d'affaires repose souvent sur la qualité de l'interaction humaine : upsell, vente complémentaire, vente émotionnelle. Le client mystère permet de révéler, tester et améliorer la mise en pratique de ces stratégies commerciales.",
      obj2Title: 'Motiver et Valoriser les Équipes',
      obj2Desc: "Un rapport client mystère met en lumière davantage de points positifs que de constats négatifs. Le débriefing permet de valoriser les bonnes pratiques et de féliciter les équipes performantes.",
      obj3Title: 'Améliorer la Réputation en Ligne',
      obj3Desc: "Les avis en ligne (Google, Tripadvisor, Yelp) sont souvent pénalisants mais manquent de précision. Une étude client mystère offre une analyse fine et détaillée pour identifier et corriger les dysfonctionnements, améliorant ainsi l'expérience client.",
      obj4Title: 'Contrôler les Encaissements',
      obj4Desc: "La manipulation d'espèces et de valeurs comporte des risques. Un scénario client mystère spécifiquement orienté encaissements permet de sécuriser ces processus et de prévenir les pertes.",
      packTag: 'Offre Complète',
      packTitle: 'Le Pack Client Mystère Tout Inclus',
      packIntro: "Notre offre est conçue pour être simple, complète et sans surprise. Chaque pack inclut l'ensemble des prestations nécessaires à une étude client mystère réussie.",
      packItem1: "Préparation et personnalisation de l'étude selon votre secteur d'activité et votre enseigne (questionnaire, scénario, méthode d'intervention)",
      packItem2: 'Visites mystères planifiées selon votre calendrier',
      packItem3: 'Rapports de visites détaillés',
      packItem4: 'En option : achat ou consommation par le client mystère',
      packItem5: 'En option : achat ou consommation par le client mystère',
      guaranteeTitle: '',
      guaranteeDesc: '',
      guaranteeText: '',
      ctaTitle: 'Prêt à Améliorer Votre Service Client ?',
      ctaText: "Découvrez nos formules tarifaires adaptées à tous les besoins et toutes les tailles d'entreprise.",
      seePricing: 'Voir les Tarifs'
    },
    cmTarifs: {
      heroTitle: 'Nos Tarifs Client Mystère',
      heroSubtitle: 'Des formules tout compris, adaptées à votre volume de visites. Plus vous commandez, plus le tarif par visite diminue.',
      backToExplanation: 'Retour aux explications',
      pricingTag: 'Nos Formules',
      pricingTitle: 'Choisissez Votre Pack',
      allInclusive: 'Pack tout compris',
      eurPerVisit: '€ / visite',
      visit: 'visite',
      visitsMin: 'visites minimum',
      feat1: 'Questionnaire personnalisé',
      feat2: 'Visites sur site ou par téléphone',
      feat3: 'Rapport détaillé par visite',
      feat4: 'Accompagnement personnalisé',
      order: 'Commander',
      includedTag: 'Tout est Inclus',
      includedTitle: 'Ce Que Comprend Chaque Pack',
      inc1: "Personnalisation complète de l'étude (questionnaire, scénario, profil)",
      inc2: 'Planification flexible selon votre calendrier',
      inc3: 'Rapports détaillés après chaque visite',
      inc4: 'Accompagnement personnalisé',
      guarantee: '',
      ctaTitle: 'Besoin d\'un Devis Personnalisé ?',
      ctaText: "Pour des besoins spécifiques ou un volume important, contactez-nous directement pour une offre sur mesure.",
      contactUs: 'Nous Contacter',
      onlineDiscountTitle: '-30% sur toutes les interventions en ligne',
      onlineDiscountDesc: 'Appels mystères, évaluations téléphoniques et visites en ligne : bénéficiez d\'une remise de 30% par rapport aux tarifs des interventions physiques.'
    },
    conseils: {
      tagline: 'Nos Conseils',
      heroTitle: 'Conseils Stratégiques pour Votre Entreprise',
      heroSubtitle: 'Un accompagnement sur mesure pour développer votre activité, optimiser vos performances et garantir votre conformité.',
      backToPro: 'Retour aux services professionnels',
      seeOffer: "Voir l'offre détaillée",
      ctaTitle: 'Besoin d\'un Conseil Personnalisé ?',
      ctaText: 'Contactez-nous pour discuter de vos besoins spécifiques et obtenir une proposition adaptée.',
      contactUs: 'Nous Contacter'
    },
    formations: {
      tagline: 'Nos Formations',
      heroTitle: 'Formations Professionnelles Certifiantes',
      heroSubtitle: 'Des formations techniques et qualifiantes pour accompagner votre montée en compétences et celle de vos équipes.',
      backToPro: 'Retour aux services professionnels',
      levels: 'Niveaux',
      program: 'Programme',
      aiprTitle: 'AIPR - Réseaux',
      aiprDuration: '1 à 2 jours',
      aiprDesc: "Formation aux Autorisations d'Intervention à Proximité des Réseaux (AIPR). Obligation légale pour toute personne intervenant à proximité de réseaux aériens ou enterrés.",
      aiprLevel1: 'Concepteur',
      aiprLevel2: 'Encadrant',
      aiprLevel3: 'Opérateur',
      aiprDetail1: "Réglementation et obligations légales liées aux travaux à proximité des réseaux",
      aiprDetail2: "Préparation à l'examen QCM pour l'obtention de l'AIPR",
      elecTitle: 'Habilitations Électriques BT',
      elecDuration: '1 à 3 jours',
      elecDesc: "Formation aux habilitations électriques Basse Tension, obligatoire pour toute personne effectuant ou supervisant des opérations électriques.",
      elecLevel1: 'B0 / H0 / H0V',
      elecLevel2: 'BS / BE Manœuvre',
      elecLevel3: 'B1 / B1V / B2 / B2V',
      elecLevel4: 'BR / BC',
      elecDetail1: 'Connaître les risques électriques et savoir se protéger',
      elecDetail2: 'Effectuer ou superviser des opérations électriques en sécurité',
      elecDetail3: "Réaliser des consignations et des interventions en basse tension",
      elecDetail4: "Préparation à l'évaluation pour l'obtention du titre d'habilitation",
      telecomTitle: 'Télécom - Fibre Optique',
      telecomDuration: '3 mois',
      telecomDesc: "Formation complète aux techniques de raccordement, de tirage et de mesure de la fibre optique pour les réseaux de télécommunication.",
      telecomDetail1: 'Réalisation et lecture de plans de fibre optique',
      telecomDetail2: 'Tirage des câbles en aérien, souterrain, intérieur et en façade',
      telecomDetail3: 'Raccordement par fusion',
      telecomDetail4: 'Mesures et tests avec réflectomètre (OTDR) et photomètre',
      telecomDetail5: 'Raccordement d\'abonnés (PBO, PTO, DTIo)',
      telecomDetail6: 'Utilisation des équipements professionnels de terrain',
      domotiqueTitle: 'Technicien des Bâtiments Communicants et Connectés',
      domotiqueDuration: '6 mois',
      domotiqueDesc: "Formation à la gestion technique du bâtiment (GTB), la domotique et les objets connectés. Installation, paramétrage et maintenance des systèmes intelligents.",
      domotiqueDetail1: 'Gestion technique du bâtiment (GTB) et gestion technique centralisée (GTC)',
      domotiqueDetail2: 'Installation et paramétrage de systèmes domotiques (chauffage, climatisation, ventilation, éclairage)',
      domotiqueDetail3: 'Automatisation : volets roulants, portails, éclairage intelligent, station météo',
      domotiqueDetail4: 'Capteurs de présence et de mouvement, alarme et vidéosurveillance',
      domotiqueDetail5: 'Paramétrage d\'objets connectés à l\'aide du protocole KNX',
      domotiqueDetail6: 'Maintenance préventive et curative des installations',
      ctaTitle: 'Intéressé par une Formation ?',
      ctaText: 'Contactez-nous pour connaître les prochaines sessions, les tarifs et les modalités de financement.',
      contactUs: 'Nous Contacter'
    },
    contact: {
      title: 'Contactez-Nous',
      subtitle: 'Notre équipe est à votre disposition pour répondre à toutes vos questions',
      info: 'Informations',
      stayInTouch: 'Restons en Contact',
      infoText: "N'hésitez pas à nous contacter pour toute question concernant nos services. Nous vous répondrons dans les plus brefs délais.",
      phone: 'Téléphone',
      whatsappNote: 'Si nous ne sommes pas joignables, veuillez envoyer un message avec le détail de votre demande via WhatsApp.',
      email: 'Email',
      address: 'Adresse',
      hours: "Horaires d'Ouverture",
      weekdays: 'Lundi - Vendredi',
      saturday: 'Samedi',
      sunday: 'Dimanche',
      byAppointment: 'Sur rendez-vous',
      closed: 'Fermé',
      form: 'Formulaire',
      sendMessage: 'Envoyez-nous un Message',
      firstName: 'Prénom',
      lastName: 'Nom',
      yourFirstName: 'Votre prénom',
      yourLastName: 'Votre nom',
      yourEmail: 'votre@email.com',
      phonePlaceholder: '+33 6 12 34 56 78',
      service: 'Service concerné',
      selectService: 'Sélectionnez un service',
      message: 'Message',
      messagePlaceholder: 'Décrivez votre projet ou votre demande...',
      send: 'Envoyer le Message',
      sending: 'Envoi en cours...',
      success: 'Message Envoyé!',
      successText: 'Merci de nous avoir contacté. Notre équipe vous répondra dans les plus brefs délais.',
      sendAnother: 'Envoyer un autre message',
      services: {
        visaSchengen: 'Visa Schengen',
        visaCanada: 'Visa Canada',
        gestionImmo: 'Gestion Immobilière',
        maitriseOeuvre: "Maîtrise d'Œuvre",
        strategie: 'Stratégie Commerciale',
        formation: 'Formation des Équipes',
        hygiene: 'Hygiène et Nettoyage Industriel',
        clientMystere: 'Client Mystère',
        marketing: 'Marketing et Création Digitale',
        other: 'Autre demande'
      }
    },
    footer: {
      description: "Votre partenaire de confiance pour vos démarches de visa et vos besoins en conseil d'entreprise.",
      particuliers: 'Particuliers',
      professionnels: 'Professionnels',
      contact: 'Contact',
      visaSchengen: 'Visa Schengen',
      visaCanada: 'Visa Canada',
      accompagnement: 'Accompagnement',
      gestionImmo: 'Gestion Immobilière',
      maitriseOeuvre: "Maîtrise d'Œuvre",
      strategie: 'Stratégie Commerciale',
      formations: 'Formations',
      hygiene: 'Hygiène et Nettoyage Industriel',
      clientMystere: 'Client Mystère',
      marketing: 'Marketing Digital',
      rights: 'Tous droits réservés.',
      legal: 'Mentions Légales',
      privacy: 'Politique de Confidentialité',
      taxNotice: 'Tous les prix affichés sur le site ou communiqués par nos agents sont hors taxes (HT). TVA applicable : 19%.'
    }
  },
  en: {
    nav: {
      home: 'Home',
      particuliers: 'Individuals',
      professionnels: 'Professionals',
      contact: 'Contact',
      contactUs: 'Contact Us'
    },
    home: {
      space: 'Space',
      particuliers: 'Individuals',
      professionnels: 'Professionals',
      particuliersDesc: 'Advice and support for your Schengen and Canada visa procedures',
      professionnelsDesc: 'Commercial strategy, training and industrial hygiene consulting',
      discover: 'Discover',
      about: 'About Us',
      aboutTitle: 'Excellence at Your Service',
      aboutText1: 'Excelsium Conseils is your trusted partner for all your immigration and business development projects. Our recognized expertise and commitment to excellence enable us to effectively support you in all your endeavors.',
      aboutText2: 'Whether you are an individual seeking personalized support for your visa procedures, or a company looking to optimize its performance, we put our expertise at your service.',
      yearsExp: 'Years of Experience',
      whyUs: 'Why Choose Us',
      commitment: 'Our Quality Commitment',
      expertise: 'Expertise',
      expertiseDesc: 'A team of expert consultants in their respective fields',
      support: 'Support',
      supportDesc: 'Personalized follow-up throughout your procedures',
      reactivity: 'Reactivity',
      reactivityDesc: 'Quick responses and efficient processing of your files',
      trust: 'Trust',
      trustDesc: 'Transparency and confidentiality guaranteed in all our services',
      ready: 'Ready to Start?',
      together: 'Let\'s Achieve Your Projects Together',
      ctaText: 'Contact us today for a consultation and discover how Excelsium Conseils can help you succeed.',
      ourServices: 'Our Services',
      teamBanner: 'A multicultural and experienced team of French, Canadian and Tunisian talents, dedicated to your success.',
      teamHighlight: 'Excelsium Conseils'
    },
    particuliers: {
      title: 'Our Services for Individuals',
      subtitle: 'Visa & Immigration, Property Management and Project Management',
      services: 'Our Services',
      complete: 'Complete Support',
      visaSchengen: 'Schengen Visa',
      visaSchengenDesc: 'Support for your visa applications to the 27 Schengen area countries',
      visaCanada: 'Canada Visa',
      visaCanadaDesc: 'Tourist, student, work or permanent residence in Canada',
      gestionImmo: 'Property Management',
      gestionImmoDesc: 'Complete rental management and monitoring of your real estate assets',
      maitriseOeuvre: 'Project Management',
      maitriseOeuvreDesc: 'Management of your construction and renovation projects',
      service: 'Service',
      successRate: 'Success Rate',
      ourSuccessRate: 'Our Success Rate',
      successRateValue: '+75%',
      successRateLabel: 'of approved applications',
      vsCompetitors: 'Average refusal rate',
      competitorRate: '~40%',
      competitorRateLabel: 'with other providers',
      successRateText: 'Thanks to our expertise and personalized support, we achieve a success rate well above the market average. Where many applications are rejected due to inadequate preparation, we maximize your chances of success.',
      gestionImmoTitle: 'Property Management',
      gestionImmoText: 'Entrust us with the management of your real estate with complete peace of mind. Our team ensures rigorous and personalized monitoring of your assets.',
      gestionLocative: 'Complete rental management',
      rechercheLocataires: 'Tenant search and selection',
      suiviLoyers: 'Rent and charges monitoring',
      entretien: 'Property maintenance',
      maitriseOeuvreTitle: 'Project Management',
      maitriseOeuvreText: 'We manage your construction and renovation projects from A to Z, guaranteeing quality, deadlines and budget.',
      conception: 'Design and technical studies',
      coordination: 'Work coordination',
      suiviChantier: 'Site monitoring',
      reception: 'Reception and quality control',
      schengenText: 'The Schengen area comprises 27 European countries allowing free movement. Our Schengen visa support service includes:',
      analyseConseil: 'Analysis of your situation and personalized advice',
      constitution: 'Complete file preparation',
      verification: 'Document verification and optimization',
      preparation: 'Obtaining your appointment with the competent authorities for visa issuance',
      suivi: 'Application tracking',
      schengenCountries: 'Schengen Area Countries',
      canadaText: 'Canada offers many immigration opportunities. We support you for different types of visas:',
      visaTouriste: 'Tourist Visa',
      visitCanada: 'Visit and discover Canada',
      visaEtudiant: 'Student Visa',
      etudes: 'Studies in Canadian universities',
      workvisa: 'Work Permit',
      workOpportunities: 'Professional opportunities',
      residence: 'Permanent Residence',
      immigration: 'Permanent immigration to Canada',
      visaMedical: 'Medical Visit Visa',
      medicalDesc: 'Medical care and treatment in Canada',
      visaFamille: 'Family / Friends Visit Visa',
      familleDesc: 'Visit your loved ones residing in Canada',
      process: 'Our Process',
      howWeWork: 'How We Work',
      consultation: 'Consultation',
      consultationDesc: 'Free analysis of your situation and immigration goals',
      preparationStep: 'Preparation',
      preparationStepDesc: 'Complete file preparation and verification',
      submission: 'Submission',
      submissionDesc: 'Filing your application and follow-up with authorities',
      supportStep: 'Support',
      supportStepDesc: 'Continuous support until your appointment with the consular authorities or their representatives',
      readyToStart: 'Ready to Start Your Project?',
      contactForConsultation: 'Contact us for a consultation and discover how we can help you achieve your immigration project.',
      requestConsultation: 'Request a Consultation',
      visaContactTitle: 'Visa Contact',
      visaContactSubtitle: 'Contact Our Visa Service',
      visaPhone: 'WhatsApp Visa',
      whatsappOnly: 'Reachable via WhatsApp only',
      visaEmail: 'Visa Email',
      visaEmailDesc: 'For all visa-related inquiries',
      domotique: 'Home Automation Consulting',
      domotiqueDesc: 'Optimize your comfort and energy consumption with our advice on home automation and smart buildings.',
      domotiqueDetail1: 'Building Management Systems (BMS)',
      domotiqueDetail2: 'Centralized technical management (heating, AC, ventilation, lighting)',
      domotiqueDetail3: 'Installation optimization (roller shutters, gates, lighting, weather station)',
      domotiqueDetail4: 'Presence and motion sensors, alarm and video surveillance',
      domotiqueDetail5: 'Connected object configuration',
      domotiqueDetail6: 'Personalized advice for your smart home project'
    },
    professionnels: {
      title: 'Consulting & Training',
      conseils: 'Consulting',
      formationsLink: 'Training',
      subtitle: 'Commercial strategy, team training, fibre and electrical certification consulting, and industrial hygiene',
      services: 'Our Services',
      solutions: 'Tailored Solutions for Your Business',
      strategie: 'Commercial Strategy',
      strategieDesc: 'Develop your business with a tailor-made commercial strategy adapted to your market and objectives.',
      analyseMarche: 'Market analysis and positioning',
      objectifs: 'Definition of commercial objectives',
      planAction: 'Action plan and implementation',
      suiviKPI: 'Performance indicators monitoring',
      optimisation: 'Continuous strategy optimization',
      formation: 'Team Training',
      formationDesc: 'Strengthen your sales teams\' skills to achieve excellence.',
      techniquesVente: 'Advanced sales techniques',
      negociation: 'Commercial negotiation',
      relationClient: 'Customer relationship management',
      leadership: 'Commercial leadership',
      coaching: 'Individualized coaching',
      hygiene: 'Industrial Hygiene & Cleaning',
      hygieneDesc: 'Ensure compliance and safety of your industrial processes in terms of hygiene and cleaning.',
      auditInstallations: 'Facility audit',
      conformite: 'Regulatory compliance',
      plansSanitaires: 'Sanitary control plans',
      formationPersonnel: 'Staff training',
      certification: 'Certification support',
      clientMystere: 'Mystery Shopper',
      clientMystereDesc: 'Evaluate the quality of your customer service and shopping experience through our mystery visits.',
      visitesMysteres: 'Mystery visits at point of sale',
      appelsMysteres: 'Mystery calls and contacts',
      evaluationAccueil: 'Reception and advice evaluation',
      rapportRecommandations: 'Detailed report with recommendations',
      suiviAmelioration: 'Improvement plan follow-up',
      marketing: 'Digital Marketing & Creation',
      marketingDesc: 'Boost your online presence and develop your brand image with our digital solutions.',
      strategieDigital: 'Digital marketing strategy',
      creationSites: 'Website and landing page creation',
      reseauxSociaux: 'Social media management',
      creationContenu: 'Content creation and graphic design',
      campagnesPub: 'Online advertising campaigns',
      expertise: 'Our Expertise',
      accelerate: 'Accelerate Your Business Growth',
      expertiseText1: 'Excelsium Conseils supports companies in their commercial development and compliance. Our personalized approach and sector expertise allow us to offer solutions adapted to your challenges.',
      expertiseText2: 'From defining your commercial strategy to training your teams, through industrial hygiene consulting, we are your trusted partner to achieve excellence.',
      entreprises: 'Companies Supported',
      satisfaction: 'Customer Satisfaction',
      secteurs: 'Business Sectors',
      results: 'Our Results',
      concreteResults: 'Concrete Results for Your Business',
      resultsText: 'Our results-oriented approach allows us to generate measurable value for our clients. We commit to clear objectives and support their achievement.',
      methodology: 'Proven and adaptable methodology',
      expertsConsultants: 'Expert consultants in their field',
      personalizedSupport: 'Personalized support and continuous follow-up',
      croissance: 'Average revenue growth',
      auditsReussis: 'Successful audits',
      collaborateurs: 'Employees trained',
      recommandation: 'Recommendation rate',
      multiSector: 'Multi-Sector Expertise',
      readyToDevelop: 'Ready to Develop Your Business?',
      contactForAnalysis: 'Contact us for a free needs analysis and discover how Excelsium Conseils can support your business growth.',
      requestQuote: 'Request a Quote',
      seeDetails: 'See offers'
    },
    cm: {
      tagline: 'Mystery Shopper',
      heroTitle: 'Mystery Shopping at the Service of Your Excellence',
      heroSubtitle: 'Objectively evaluate the quality of your reception, customer service and shopping experience through our professional mystery visits.',
      simpleProcess: 'Simple and Effective',
      howItWorks: 'How It Works',
      step1Title: 'Preparation',
      step1Desc: 'We customize the parameters of your mystery visits together: schedule, evaluation questionnaire, profile and scenario of your mystery shoppers, adapted to your industry.',
      step2Title: 'Mystery Visits',
      step2Desc: 'On-site and/or by phone, our mystery shoppers go into action. They behave like any regular customer, evaluating every aspect of the experience.',
      step3Title: 'Results & Analysis',
      step3Desc: 'You receive a detailed report after each mystery visit. Analyze your results online: sort, compare, filter and export data for optimal tracking.',
      objectivesTag: 'Why Mystery Shopping',
      objectivesTitle: 'Four Strategic Objectives',
      obj1Title: 'Increase Revenue',
      obj1Desc: 'Revenue growth often depends on the quality of human interaction: upselling, cross-selling, emotional selling. Mystery shopping reveals, tests and improves the implementation of these commercial strategies.',
      obj2Title: 'Motivate and Reward Teams',
      obj2Desc: 'A mystery shopper report highlights more positive points than negative findings. Debriefing helps showcase best practices and reward high-performing teams.',
      obj3Title: 'Improve Online Reputation',
      obj3Desc: 'Online reviews (Google, Tripadvisor, Yelp) are often penalizing but lack precision. A mystery shopping study provides a detailed analysis to identify and fix issues, improving the customer experience.',
      obj4Title: 'Control Cash Handling',
      obj4Desc: 'Handling cash and valuables always carries risks. A mystery shopping scenario specifically focused on cash transactions helps secure these processes and prevent losses.',
      packTag: 'Complete Offer',
      packTitle: 'The All-Inclusive Mystery Shopper Pack',
      packIntro: 'Our offer is designed to be simple, complete and with no surprises. Each pack includes all the services needed for a successful mystery shopping study.',
      packItem1: 'Preparation and customization of the study according to your industry and brand (questionnaire, scenario, intervention method)',
      packItem2: 'Mystery visits scheduled according to your calendar',
      packItem3: 'Detailed visit reports',
      packItem4: 'Optional: purchase or consumption by the mystery shopper',
      packItem5: 'Optional: purchase or consumption by the mystery shopper',
      guaranteeTitle: '',
      guaranteeDesc: '',
      guaranteeText: '',
      ctaTitle: 'Ready to Improve Your Customer Service?',
      ctaText: 'Discover our pricing plans adapted to all needs and business sizes.',
      seePricing: 'See Pricing'
    },
    cmTarifs: {
      heroTitle: 'Mystery Shopper Pricing',
      heroSubtitle: 'All-inclusive plans adapted to your visit volume. The more you order, the lower the price per visit.',
      backToExplanation: 'Back to explanations',
      pricingTag: 'Our Plans',
      pricingTitle: 'Choose Your Pack',
      allInclusive: 'All-inclusive pack',
      eurPerVisit: '€ / visit',
      visit: 'visit',
      visitsMin: 'visits minimum',
      feat1: 'Customized questionnaire',
      feat2: 'On-site or phone visits',
      feat3: 'Detailed report per visit',
      feat4: 'Personalized support',
      order: 'Order',
      includedTag: 'Everything Included',
      includedTitle: 'What Each Pack Includes',
      inc1: 'Full customization of the study (questionnaire, scenario, profile)',
      inc2: 'Flexible scheduling according to your calendar',
      inc3: 'Detailed reports after each visit',
      inc4: 'Personalized support',
      guarantee: '',
      ctaTitle: 'Need a Custom Quote?',
      ctaText: 'For specific needs or large volumes, contact us directly for a tailor-made offer.',
      contactUs: 'Contact Us',
      onlineDiscountTitle: '-30% on all online interventions',
      onlineDiscountDesc: 'Mystery calls, phone evaluations and online visits: enjoy a 30% discount compared to on-site intervention rates.'
    },
    conseils: {
      tagline: 'Our Consulting',
      heroTitle: 'Strategic Consulting for Your Business',
      heroSubtitle: 'Tailored support to grow your business, optimize performance and ensure compliance.',
      backToPro: 'Back to professional services',
      seeOffer: 'See detailed offer',
      ctaTitle: 'Need Personalized Advice?',
      ctaText: 'Contact us to discuss your specific needs and get a tailored proposal.',
      contactUs: 'Contact Us'
    },
    formations: {
      tagline: 'Our Training',
      heroTitle: 'Certified Professional Training',
      heroSubtitle: 'Technical and qualifying training to support the skill development of you and your teams.',
      backToPro: 'Back to professional services',
      levels: 'Levels',
      program: 'Program',
      aiprTitle: 'AIPR - Network Safety',
      aiprDuration: '1 to 2 days',
      aiprDesc: 'Training for Authorization to Work Near Networks (AIPR). Legal requirement for anyone working near overhead or underground networks.',
      aiprLevel1: 'Designer', aiprLevel2: 'Supervisor', aiprLevel3: 'Operator',
      aiprDetail1: 'Regulations and legal obligations for work near networks',
      aiprDetail2: 'Preparation for the MCQ exam to obtain the AIPR',
      elecTitle: 'Electrical Certifications BT',
      elecDuration: '1 to 3 days',
      elecDesc: 'Low Voltage electrical certification training, mandatory for anyone performing or supervising electrical operations.',
      elecLevel1: 'B0 / H0 / H0V', elecLevel2: 'BS / BE Operation', elecLevel3: 'B1 / B1V / B2 / B2V', elecLevel4: 'BR / BC',
      elecDetail1: 'Know electrical risks and how to protect yourself',
      elecDetail2: 'Perform or supervise electrical operations safely',
      elecDetail3: 'Carry out lockout/tagout and low voltage interventions',
      elecDetail4: 'Preparation for the evaluation to obtain certification',
      telecomTitle: 'Telecom - Fiber Optics',
      telecomDuration: '3 months',
      telecomDesc: 'Complete training in fiber optic splicing, pulling and measurement techniques for telecommunications networks.',
      telecomDetail1: 'Fiber optic plan reading and creation',
      telecomDetail2: 'Aerial, underground, indoor and facade cable pulling',
      telecomDetail3: 'Fusion splicing',
      telecomDetail4: 'Measurements and tests with OTDR reflectometer and power meter',
      telecomDetail5: 'Subscriber connection (PBO, PTO, DTIo)',
      telecomDetail6: 'Use of professional field equipment',
      domotiqueTitle: 'Communicating and Connected Buildings Technician',
      domotiqueDuration: '6 months',
      domotiqueDesc: 'Training in building management systems (BMS), home automation and connected objects. Installation, configuration and maintenance of smart systems.',
      domotiqueDetail1: 'Building Management Systems (BMS) and Centralized Technical Management (CTM)',
      domotiqueDetail2: 'Installation and configuration of home automation (heating, AC, ventilation, lighting)',
      domotiqueDetail3: 'Automation: roller shutters, gates, smart lighting, weather station',
      domotiqueDetail4: 'Presence and motion sensors, alarm and video surveillance',
      domotiqueDetail5: 'Connected object configuration using KNX protocol',
      domotiqueDetail6: 'Preventive and corrective maintenance of installations',
      ctaTitle: 'Interested in Training?',
      ctaText: 'Contact us to learn about upcoming sessions, pricing and funding options.',
      contactUs: 'Contact Us'
    },
    contact: {
      title: 'Contact Us',
      subtitle: 'Our team is at your disposal to answer all your questions',
      info: 'Information',
      stayInTouch: 'Stay in Touch',
      infoText: 'Do not hesitate to contact us for any questions regarding our services. We will respond as soon as possible.',
      phone: 'Phone',
      whatsappNote: 'If we are not available, please send a message with the details of your request via WhatsApp.',
      email: 'Email',
      address: 'Address',
      hours: 'Opening Hours',
      weekdays: 'Monday - Friday',
      saturday: 'Saturday',
      sunday: 'Sunday',
      byAppointment: 'By appointment',
      closed: 'Closed',
      form: 'Form',
      sendMessage: 'Send us a Message',
      firstName: 'First Name',
      lastName: 'Last Name',
      yourFirstName: 'Your first name',
      yourLastName: 'Your last name',
      yourEmail: 'your@email.com',
      phonePlaceholder: '+33 6 12 34 56 78',
      service: 'Service concerned',
      selectService: 'Select a service',
      message: 'Message',
      messagePlaceholder: 'Describe your project or request...',
      send: 'Send Message',
      sending: 'Sending...',
      success: 'Message Sent!',
      successText: 'Thank you for contacting us. Our team will respond as soon as possible.',
      sendAnother: 'Send another message',
      services: {
        visaSchengen: 'Schengen Visa',
        visaCanada: 'Canada Visa',
        gestionImmo: 'Property Management',
        maitriseOeuvre: 'Project Management',
        strategie: 'Commercial Strategy',
        formation: 'Team Training',
        hygiene: 'Industrial Hygiene & Cleaning',
        clientMystere: 'Mystery Shopper',
        marketing: 'Digital Marketing & Creation',
        other: 'Other request'
      }
    },
    footer: {
      description: 'Your trusted partner for your visa procedures and business consulting needs.',
      particuliers: 'Individuals',
      professionnels: 'Professionals',
      contact: 'Contact',
      visaSchengen: 'Schengen Visa',
      visaCanada: 'Canada Visa',
      accompagnement: 'Support',
      gestionImmo: 'Property Management',
      maitriseOeuvre: 'Project Management',
      strategie: 'Commercial Strategy',
      formations: 'Training',
      hygiene: 'Industrial Hygiene & Cleaning',
      clientMystere: 'Mystery Shopper',
      marketing: 'Digital Marketing',
      rights: 'All rights reserved.',
      legal: 'Legal Notice',
      privacy: 'Privacy Policy',
      taxNotice: 'All prices displayed on the website or communicated by our agents are exclusive of tax (excl. VAT). Applicable VAT: 19%.'
    }
  },
  ar: {
    nav: {
      home: 'الرئيسية',
      particuliers: 'الأفراد',
      professionnels: 'المهنيون',
      contact: 'اتصل بنا',
      contactUs: 'تواصل معنا'
    },
    home: {
      space: 'قسم',
      particuliers: 'الأفراد',
      professionnels: 'المهنيون',
      particuliersDesc: 'استشارات ومرافقة لإجراءات تأشيرة شنغن وكندا',
      professionnelsDesc: 'استراتيجية تجارية، تدريب واستشارات النظافة الصناعية',
      discover: 'اكتشف',
      about: 'من نحن',
      aboutTitle: 'التميز في خدمة نجاحكم',
      aboutText1: 'إكسلسيوم للاستشارات هو شريككم الموثوق لجميع مشاريع الهجرة وتطوير الأعمال. خبرتنا المعترف بها والتزامنا بالتميز يمكننا من مرافقتكم بفعالية في جميع إجراءاتكم.',
      aboutText2: 'سواء كنتم أفراداً تبحثون عن مرافقة شخصية لإجراءات التأشيرة، أو شركة ترغب في تحسين أدائها، نضع خبرتنا في خدمتكم.',
      yearsExp: 'سنوات من الخبرة',
      whyUs: 'لماذا تختاروننا',
      commitment: 'التزامنا بالجودة',
      expertise: 'الخبرة',
      expertiseDesc: 'فريق من المستشارين الخبراء في مجالاتهم',
      support: 'المرافقة',
      supportDesc: 'متابعة شخصية طوال إجراءاتكم',
      reactivity: 'سرعة الاستجابة',
      reactivityDesc: 'ردود سريعة ومعالجة فعالة لملفاتكم',
      trust: 'الثقة',
      trustDesc: 'الشفافية والسرية مضمونة في جميع خدماتنا',
      ready: 'مستعد للبدء؟',
      together: 'لنحقق مشاريعكم معاً',
      ctaText: 'اتصلوا بنا اليوم للحصول على استشارة واكتشفوا كيف يمكن لإكسلسيوم مساعدتكم على النجاح.',
      ourServices: 'خدماتنا',
      teamBanner: 'فريق متعدد الثقافات وذو خبرة عالية، يضم كفاءات فرنسية وكندية وتونسية، في خدمة نجاحكم.',
      teamHighlight: 'إكسلسيوم للاستشارات'
    },
    particuliers: {
      title: 'خدماتنا للأفراد',
      subtitle: 'التأشيرات والهجرة، إدارة العقارات وإدارة المشاريع',
      services: 'خدماتنا',
      complete: 'مرافقة شاملة',
      visaSchengen: 'تأشيرة شنغن',
      visaSchengenDesc: 'مرافقة لطلبات التأشيرة إلى 27 دولة في منطقة شنغن',
      visaCanada: 'تأشيرة كندا',
      visaCanadaDesc: 'سياحة، دراسة، عمل أو إقامة دائمة في كندا',
      gestionImmo: 'إدارة العقارات',
      gestionImmoDesc: 'إدارة إيجارية كاملة ومتابعة أصولكم العقارية',
      maitriseOeuvre: 'إدارة المشاريع',
      maitriseOeuvreDesc: 'إدارة مشاريع البناء والتجديد',
      service: 'خدمة',
      successRate: 'معدل النجاح',
      ourSuccessRate: 'معدل نجاحنا',
      successRateValue: '+75%',
      successRateLabel: 'من الطلبات المقبولة',
      vsCompetitors: 'معدل الرفض المتوسط',
      competitorRate: '~40%',
      competitorRateLabel: 'لدى مقدمي الخدمات الآخرين',
      successRateText: 'بفضل خبرتنا ودعمنا الشخصي، نحقق معدل نجاح أعلى بكثير من متوسط السوق. حيث يتم رفض العديد من الطلبات بسبب عدم كفاية التحضير، نحن نزيد فرص نجاحكم.',
      gestionImmoTitle: 'إدارة العقارات',
      gestionImmoText: 'اعهدوا إلينا بإدارة عقاراتكم بكل راحة بال. يضمن فريقنا متابعة دقيقة وشخصية لأصولكم.',
      gestionLocative: 'إدارة إيجارية كاملة',
      rechercheLocataires: 'البحث عن المستأجرين واختيارهم',
      suiviLoyers: 'متابعة الإيجارات والرسوم',
      entretien: 'صيانة العقارات',
      maitriseOeuvreTitle: 'إدارة المشاريع',
      maitriseOeuvreText: 'ندير مشاريع البناء والتجديد من الألف إلى الياء، مع ضمان الجودة والمواعيد والميزانية.',
      conception: 'التصميم والدراسات التقنية',
      coordination: 'تنسيق الأعمال',
      suiviChantier: 'متابعة الموقع',
      reception: 'الاستلام ومراقبة الجودة',
      schengenText: 'تضم منطقة شنغن 27 دولة أوروبية تسمح بحرية التنقل. تشمل خدمة مرافقة تأشيرة شنغن:',
      analyseConseil: 'تحليل وضعكم واستشارة شخصية',
      constitution: 'إعداد الملف الكامل',
      verification: 'التحقق من الوثائق وتحسينها',
      preparation: 'الحصول على موعدكم مع السلطات المختصة لمنح التأشيرة',
      suivi: 'متابعة طلبكم',
      schengenCountries: 'دول منطقة شنغن',
      canadaText: 'تقدم كندا العديد من فرص الهجرة. نرافقكم لمختلف أنواع التأشيرات:',
      visaTouriste: 'تأشيرة سياحية',
      visitCanada: 'زيارة واكتشاف كندا',
      visaEtudiant: 'تأشيرة طالب',
      etudes: 'الدراسة في الجامعات الكندية',
      workvisa: 'تصريح عمل',
      workOpportunities: 'فرص مهنية',
      residence: 'الإقامة الدائمة',
      immigration: 'الهجرة النهائية إلى كندا',
      process: 'عمليتنا',
      howWeWork: 'كيف نعمل',
      consultation: 'الاستشارة',
      consultationDesc: 'تحليل وضعكم وأهدافكم من الهجرة',
      preparationStep: 'التحضير',
      preparationStepDesc: 'إعداد والتحقق من ملفكم الكامل',
      submission: 'التقديم',
      submissionDesc: 'تقديم طلبكم والمتابعة مع السلطات',
      supportStep: 'المرافقة',
      supportStepDesc: 'دعم مستمر حتى موعدكم مع السلطات القنصلية أو ممثليها',
      readyToStart: 'مستعد لبدء مشروعك؟',
      contactForConsultation: 'اتصلوا بنا للحصول على استشارة واكتشفوا كيف يمكننا مساعدتكم في تحقيق مشروع الهجرة.',
      requestConsultation: 'طلب استشارة',
      visaContactTitle: 'اتصال التأشيرة',
      visaContactSubtitle: 'تواصل مع خدمة التأشيرات',
      visaPhone: 'واتساب التأشيرات',
      whatsappOnly: 'متاح فقط عبر واتساب',
      visaEmail: 'بريد التأشيرات',
      visaEmailDesc: 'لجميع الاستفسارات المتعلقة بالتأشيرات',
      domotique: 'استشارات تركيب الأتمتة المنزلية',
      domotiqueDesc: 'حسّنوا راحتكم واستهلاككم للطاقة من خلال استشاراتنا في الأتمتة المنزلية والمباني الذكية.',
      domotiqueDetail1: 'إدارة تقنية المباني', domotiqueDetail2: 'إدارة مركزية (تدفئة، تكييف، تهوية، إضاءة)', domotiqueDetail3: 'تحسين التركيبات (ستائر، بوابات، إضاءة، محطة أرصاد)', domotiqueDetail4: 'أجهزة استشعار الحركة والحضور، إنذار ومراقبة بالفيديو', domotiqueDetail5: 'ضبط الأجهزة المتصلة', domotiqueDetail6: 'استشارة مخصصة لمشروع منزلكم الذكي'
    },
    professionnels: {
      title: 'الاستشارات والتدريب',
      conseils: 'استشارات',
      formationsLink: 'تدريبات',
      subtitle: 'استراتيجية تجارية، تدريب الفرق، استشارات الألياف والتأهيل الكهربائي والنظافة الصناعية',
      services: 'خدماتنا',
      solutions: 'حلول مخصصة لشركتكم',
      strategie: 'الاستراتيجية التجارية',
      strategieDesc: 'طوروا نشاطكم باستراتيجية تجارية مخصصة تتناسب مع سوقكم وأهدافكم.',
      analyseMarche: 'تحليل السوق والتموضع',
      objectifs: 'تحديد الأهداف التجارية',
      planAction: 'خطة العمل والتنفيذ',
      suiviKPI: 'متابعة مؤشرات الأداء',
      optimisation: 'التحسين المستمر للاستراتيجية',
      formation: 'تدريب الفرق',
      formationDesc: 'عززوا مهارات فرق المبيعات لديكم لتحقيق التميز.',
      techniquesVente: 'تقنيات البيع المتقدمة',
      negociation: 'التفاوض التجاري',
      relationClient: 'إدارة علاقات العملاء',
      leadership: 'القيادة التجارية',
      coaching: 'التدريب الفردي',
      hygiene: 'النظافة والتنظيف الصناعي',
      hygieneDesc: 'اضمنوا الامتثال وسلامة عملياتكم الصناعية في مجال النظافة والتنظيف.',
      auditInstallations: 'تدقيق المنشآت',
      conformite: 'الامتثال التنظيمي',
      plansSanitaires: 'خطط الرقابة الصحية',
      formationPersonnel: 'تدريب الموظفين',
      certification: 'مرافقة الشهادات',
      clientMystere: 'العميل السري',
      clientMystereDesc: 'قيموا جودة خدمة العملاء وتجربة التسوق من خلال زياراتنا السرية.',
      visitesMysteres: 'زيارات سرية في نقاط البيع',
      appelsMysteres: 'مكالمات واتصالات سرية',
      evaluationAccueil: 'تقييم الاستقبال والنصيحة',
      rapportRecommandations: 'تقرير مفصل مع التوصيات',
      suiviAmelioration: 'متابعة خطط التحسين',
      marketing: 'التسويق والإبداع الرقمي',
      marketingDesc: 'عززوا حضوركم على الإنترنت وطوروا صورة علامتكم التجارية مع حلولنا الرقمية.',
      strategieDigital: 'استراتيجية التسويق الرقمي',
      creationSites: 'إنشاء مواقع الويب وصفحات الهبوط',
      reseauxSociaux: 'إدارة وسائل التواصل الاجتماعي',
      creationContenu: 'إنشاء المحتوى والتصميم الجرافيكي',
      campagnesPub: 'حملات إعلانية عبر الإنترنت',
      expertise: 'خبرتنا',
      accelerate: 'سرّعوا نمو شركتكم',
      expertiseText1: 'إكسلسيوم للاستشارات ترافق الشركات في تطويرها التجاري وامتثالها. نهجنا الشخصي وخبرتنا القطاعية تمكننا من تقديم حلول تتناسب مع تحدياتكم.',
      expertiseText2: 'من تحديد استراتيجيتكم التجارية إلى تدريب فرقكم، مروراً باستشارات النظافة الصناعية، نحن شريككم الموثوق لتحقيق التميز.',
      entreprises: 'شركة تمت مرافقتها',
      satisfaction: 'رضا العملاء',
      secteurs: 'قطاعات النشاط',
      results: 'نتائجنا',
      concreteResults: 'نتائج ملموسة لشركتكم',
      resultsText: 'نهجنا الموجه نحو النتائج يمكننا من توليد قيمة قابلة للقياس لعملائنا. نلتزم بأهداف واضحة وندعم تحقيقها.',
      methodology: 'منهجية مثبتة وقابلة للتكيف',
      expertsConsultants: 'مستشارون خبراء في مجالهم',
      personalizedSupport: 'مرافقة شخصية ومتابعة مستمرة',
      croissance: 'متوسط نمو الإيرادات',
      auditsReussis: 'عمليات تدقيق ناجحة',
      collaborateurs: 'موظف تم تدريبهم',
      recommandation: 'معدل التوصية',
      multiSector: 'خبرة متعددة القطاعات',
      readyToDevelop: 'مستعد لتطوير نشاطك؟',
      contactForAnalysis: 'اتصلوا بنا للحصول على تحليل لاحتياجاتكم واكتشفوا كيف يمكن لإكسلسيوم مرافقة نمو شركتكم.',
      requestQuote: 'طلب عرض أسعار',
      seeDetails: 'اطلع على العروض'
    },
    cm: {
      tagline: 'العميل السري',
      heroTitle: 'العميل السري في خدمة تميزكم',
      heroSubtitle: 'قيموا بشكل موضوعي جودة استقبالكم وخدمة العملاء وتجربة الشراء من خلال زياراتنا السرية المهنية.',
      simpleProcess: 'بسيط وفعال',
      howItWorks: 'كيف يعمل',
      step1Title: 'التحضير',
      step1Desc: 'نخصص معاً معايير زياراتكم السرية: الجدول الزمني، استبيان التقييم، ملف وسيناريو العملاء السريين، المكيفة مع قطاع نشاطكم.',
      step2Title: 'الزيارات السرية',
      step2Desc: 'في الموقع و/أو عبر الهاتف، يبدأ عملاؤنا السريون في العمل. يتصرفون كأي عميل عادي، ويقيمون كل جانب من جوانب التجربة.',
      step3Title: 'النتائج والتحليل',
      step3Desc: 'تتلقون تقريراً مفصلاً بعد كل زيارة سرية. حللوا نتائجكم عبر الإنترنت: ترتيب، مقارنة، تصفية وتصدير البيانات للمتابعة المثلى.',
      objectivesTag: 'لماذا العميل السري',
      objectivesTitle: 'أربعة أهداف استراتيجية',
      obj1Title: 'تطوير رقم الأعمال',
      obj1Desc: 'غالباً ما يعتمد نمو رقم الأعمال على جودة التفاعل البشري: البيع الإضافي، البيع التكميلي، البيع العاطفي. يكشف العميل السري ويختبر ويحسن تطبيق هذه الاستراتيجيات التجارية.',
      obj2Title: 'تحفيز ومكافأة الفرق',
      obj2Desc: 'يبرز تقرير العميل السري نقاطاً إيجابية أكثر من الملاحظات السلبية. يتيح التقييم تسليط الضوء على الممارسات الجيدة وتكريم الفرق المتميزة.',
      obj3Title: 'تحسين السمعة على الإنترنت',
      obj3Desc: 'التقييمات عبر الإنترنت (Google, Tripadvisor, Yelp) غالباً ما تكون سلبية لكنها تفتقر للدقة. توفر دراسة العميل السري تحليلاً دقيقاً ومفصلاً لتحديد وإصلاح المشكلات.',
      obj4Title: 'مراقبة التحصيلات',
      obj4Desc: 'التعامل مع النقد والقيم ينطوي دائماً على مخاطر. سيناريو عميل سري موجه خصيصاً للتحصيلات يساعد على تأمين هذه العمليات ومنع الخسائر.',
      packTag: 'عرض شامل',
      packTitle: 'باقة العميل السري الشاملة',
      packIntro: 'عرضنا مصمم ليكون بسيطاً وشاملاً وبدون مفاجآت. كل باقة تشمل جميع الخدمات اللازمة لدراسة عميل سري ناجحة.',
      packItem1: 'إعداد وتخصيص الدراسة حسب قطاع نشاطكم وعلامتكم التجارية (استبيان، سيناريو، طريقة التدخل)',
      packItem2: 'زيارات سرية مخططة وفقاً لجدولكم الزمني',
      packItem3: 'تقارير زيارات مفصلة',
      packItem4: 'اختياري: شراء أو استهلاك من قبل العميل السري',
      packItem5: 'اختياري: شراء أو استهلاك من قبل العميل السري',
      guaranteeTitle: '',
      guaranteeDesc: '',
      guaranteeText: '',
      ctaTitle: 'مستعد لتحسين خدمة العملاء؟',
      ctaText: 'اكتشفوا خططنا التسعيرية المناسبة لجميع الاحتياجات وأحجام الشركات.',
      seePricing: 'عرض الأسعار'
    },
    cmTarifs: {
      heroTitle: 'أسعار العميل السري',
      heroSubtitle: 'خطط شاملة مكيفة حسب حجم زياراتكم. كلما زاد الطلب، انخفض السعر لكل زيارة.',
      backToExplanation: 'العودة إلى الشرح',
      pricingTag: 'خططنا',
      pricingTitle: 'اختاروا باقتكم',
      allInclusive: 'باقة شاملة',
      eurPerVisit: '€ / زيارة',
      visit: 'زيارة',
      visitsMin: 'زيارات كحد أدنى',
      feat1: 'استبيان مخصص',
      feat2: 'زيارات ميدانية أو هاتفية',
      feat3: 'تقرير مفصل لكل زيارة',
      feat4: 'مرافقة شخصية',
      order: 'اطلب الآن',
      includedTag: 'كل شيء مشمول',
      includedTitle: 'ما تتضمنه كل باقة',
      inc1: 'تخصيص كامل للدراسة (استبيان، سيناريو، ملف)',
      inc2: 'جدولة مرنة حسب تقويمكم',
      inc3: 'تقارير مفصلة بعد كل زيارة',
      inc4: 'مرافقة شخصية',
      guarantee: '',
      ctaTitle: 'تحتاج عرض أسعار مخصص؟',
      ctaText: 'لاحتياجات خاصة أو حجم كبير، اتصلوا بنا مباشرة للحصول على عرض مخصص.',
      contactUs: 'اتصل بنا',
      onlineDiscountTitle: '-30% على جميع التدخلات عبر الإنترنت',
      onlineDiscountDesc: 'المكالمات السرية والتقييمات الهاتفية والزيارات عبر الإنترنت: استفيدوا من خصم 30% مقارنة بأسعار التدخلات الميدانية.'
    },
    conseils: {
      tagline: 'استشاراتنا', heroTitle: 'استشارات استراتيجية لشركتكم', heroSubtitle: 'مرافقة مخصصة لتطوير نشاطكم وتحسين أدائكم وضمان امتثالكم.', backToPro: 'العودة إلى الخدمات المهنية', seeOffer: 'عرض التفاصيل', ctaTitle: 'تحتاج استشارة مخصصة؟', ctaText: 'اتصلوا بنا لمناقشة احتياجاتكم.', contactUs: 'اتصل بنا'
    },
    formations: {
      tagline: 'تدريباتنا', heroTitle: 'تدريبات مهنية معتمدة', heroSubtitle: 'تدريبات تقنية ومؤهلة لدعم تطوير مهاراتكم ومهارات فرقكم.', backToPro: 'العودة إلى الخدمات المهنية', levels: 'المستويات', program: 'البرنامج',
      aiprTitle: 'AIPR - الشبكات', aiprDuration: '1 إلى 2 يوم', aiprDesc: 'تدريب على تراخيص العمل بالقرب من الشبكات. التزام قانوني لكل شخص يعمل بالقرب من الشبكات.', aiprLevel1: 'مصمم', aiprLevel2: 'مشرف', aiprLevel3: 'مشغل', aiprDetail1: 'اللوائح والالتزامات القانونية', aiprDetail2: 'التحضير لامتحان الحصول على AIPR',
      elecTitle: 'التأهيلات الكهربائية BT', elecDuration: '1 إلى 3 أيام', elecDesc: 'تدريب على التأهيلات الكهربائية للجهد المنخفض.', elecLevel1: 'B0 / H0 / H0V', elecLevel2: 'BS / BE', elecLevel3: 'B1 / B1V / B2 / B2V', elecLevel4: 'BR / BC', elecDetail1: 'معرفة المخاطر الكهربائية', elecDetail2: 'تنفيذ العمليات الكهربائية بأمان', elecDetail3: 'التدخلات في الجهد المنخفض', elecDetail4: 'التحضير للحصول على التأهيل',
      telecomTitle: 'اتصالات - الألياف البصرية', telecomDuration: '3 أشهر', telecomDesc: 'تدريب شامل على تقنيات الألياف البصرية.', telecomDetail1: 'قراءة وإنشاء مخططات الألياف', telecomDetail2: 'سحب الكابلات الهوائية والأرضية والداخلية والواجهة', telecomDetail3: 'الربط بالانصهار', telecomDetail4: 'القياسات والاختبارات OTDR ومقياس الطاقة الضوئية', telecomDetail5: 'ربط المشتركين', telecomDetail6: 'استخدام المعدات المهنية',
      domotiqueTitle: 'تقني المباني المتصلة والذكية', domotiqueDuration: '6 أشهر', domotiqueDesc: 'تدريب على إدارة المباني التقنية والأتمتة المنزلية.', domotiqueDetail1: 'إدارة تقنية المباني (GTB) والإدارة التقنية المركزية (GTC)', domotiqueDetail2: 'تركيب أنظمة الأتمتة', domotiqueDetail3: 'الأتمتة: ستائر، بوابات، إضاءة ذكية', domotiqueDetail4: 'أجهزة استشعار وإنذار ومراقبة', domotiqueDetail5: 'ضبط الأجهزة المتصلة باستخدام بروتوكول KNX', domotiqueDetail6: 'الصيانة الوقائية والعلاجية',
      ctaTitle: 'مهتم بالتدريب؟', ctaText: 'اتصلوا بنا لمعرفة الجلسات القادمة والأسعار.', contactUs: 'اتصل بنا'
    },
    contact: {
      title: 'اتصل بنا',
      subtitle: 'فريقنا تحت تصرفكم للإجابة على جميع أسئلتكم',
      info: 'معلومات',
      stayInTouch: 'ابقوا على تواصل',
      infoText: 'لا تترددوا في الاتصال بنا لأي سؤال يتعلق بخدماتنا. سنرد في أقرب وقت ممكن.',
      phone: 'الهاتف',
      whatsappNote: 'إذا لم نكن متاحين، يرجى إرسال رسالة مع تفاصيل طلبكم عبر واتساب.',
      email: 'البريد الإلكتروني',
      address: 'العنوان',
      hours: 'ساعات العمل',
      weekdays: 'الاثنين - الجمعة',
      saturday: 'السبت',
      sunday: 'الأحد',
      byAppointment: 'بموعد مسبق',
      closed: 'مغلق',
      form: 'النموذج',
      sendMessage: 'أرسل لنا رسالة',
      firstName: 'الاسم الأول',
      lastName: 'اسم العائلة',
      yourFirstName: 'اسمك الأول',
      yourLastName: 'اسم عائلتك',
      yourEmail: 'بريدك@email.com',
      phonePlaceholder: '+216 96 050 699',
      service: 'الخدمة المطلوبة',
      selectService: 'اختر خدمة',
      message: 'الرسالة',
      messagePlaceholder: 'صف مشروعك أو طلبك...',
      send: 'إرسال الرسالة',
      sending: 'جاري الإرسال...',
      success: 'تم إرسال الرسالة!',
      successText: 'شكراً للتواصل معنا. سيرد فريقنا في أقرب وقت ممكن.',
      sendAnother: 'إرسال رسالة أخرى',
      services: {
        visaSchengen: 'تأشيرة شنغن',
        visaCanada: 'تأشيرة كندا',
        gestionImmo: 'إدارة العقارات',
        maitriseOeuvre: 'إدارة المشاريع',
        strategie: 'الاستراتيجية التجارية',
        formation: 'تدريب الفرق',
        hygiene: 'النظافة والتنظيف الصناعي',
        clientMystere: 'العميل السري',
        marketing: 'التسويق والإبداع الرقمي',
        other: 'طلب آخر'
      }
    },
    footer: {
      description: 'شريككم الموثوق لإجراءات التأشيرة واحتياجات الاستشارات التجارية.',
      particuliers: 'الأفراد',
      professionnels: 'المهنيون',
      contact: 'اتصل بنا',
      visaSchengen: 'تأشيرة شنغن',
      visaCanada: 'تأشيرة كندا',
      accompagnement: 'المرافقة',
      gestionImmo: 'إدارة العقارات',
      maitriseOeuvre: 'إدارة المشاريع',
      strategie: 'الاستراتيجية التجارية',
      formations: 'التدريب',
      hygiene: 'النظافة والتنظيف الصناعي',
      clientMystere: 'العميل السري',
      marketing: 'التسويق الرقمي',
      rights: 'جميع الحقوق محفوظة.',
      legal: 'إشعار قانوني',
      privacy: 'سياسة الخصوصية',
      taxNotice: 'جميع الأسعار المعروضة على الموقع أو المبلغة من قبل وكلائنا هي أسعار دون احتساب الضريبة. نسبة الضريبة المطبقة: 19%.'
    }
  },
  zh: {
    nav: {
      home: '首页',
      particuliers: '个人服务',
      professionnels: '企业服务',
      contact: '联系我们',
      contactUs: '联系我们'
    },
    home: {
      space: '专区',
      particuliers: '个人',
      professionnels: '企业',
      particuliersDesc: '申根和加拿大签证咨询与协助服务',
      professionnelsDesc: '商业策略、团队培训和工业卫生咨询',
      discover: '了解更多',
      about: '关于我们',
      aboutTitle: '卓越服务，助您成功',
      aboutText1: 'Excelsium Conseils 是您移民项目和企业发展的可靠合作伙伴。凭借专业的经验和对卓越的承诺，我们为您的各项事务提供高效支持。',
      aboutText2: '无论您是寻求个性化签证服务的个人，还是希望优化业绩的企业，我们都将以专业知识为您服务。',
      yearsExp: '年经验',
      whyUs: '为什么选择我们',
      commitment: '我们的品质承诺',
      expertise: '专业能力',
      expertiseDesc: '由各领域专家顾问组成的团队',
      support: '全程陪伴',
      supportDesc: '在您整个办理过程中提供个性化跟进',
      reactivity: '高效响应',
      reactivityDesc: '快速回复，高效处理您的文件',
      trust: '信赖保障',
      trustDesc: '所有服务均保证透明和保密',
      ready: '准备开始了吗？',
      together: '让我们携手实现您的项目',
      ctaText: '立即联系我们进行咨询，了解 Excelsium Conseils 如何助您迈向成功。',
      ourServices: '我们的服务',
      teamBanner: '一支由法国、加拿大和突尼斯人才组成的多元文化资深团队，为您的成功而服务。',
      teamHighlight: 'Excelsium Conseils'
    },
    particuliers: {
      title: '个人服务',
      subtitle: '签证与移民、房产管理和工程管理',
      services: '我们的服务',
      complete: '全方位陪伴',
      visaSchengen: '申根签证',
      visaSchengenDesc: '申根区27国签证申请协助',
      visaCanada: '加拿大签证',
      visaCanadaDesc: '旅游、留学、工作或永久居留签证',
      gestionImmo: '房产管理',
      gestionImmoDesc: '全面的租赁管理和房产资产跟进',
      maitriseOeuvre: '工程管理',
      maitriseOeuvreDesc: '建设和翻新项目全程管理',
      service: '服务',
      successRate: '成功率',
      ourSuccessRate: '我们的成功率',
      successRateValue: '+75%',
      successRateLabel: '申请通过率',
      vsCompetitors: '平均拒签率',
      competitorRate: '~40%',
      competitorRateLabel: '其他服务商',
      successRateText: '凭借我们的专业知识和个性化服务，我们的成功率远高于市场平均水平。当许多申请因准备不足而被拒时，我们最大限度地提高您的成功机会。',
      gestionImmoTitle: '房产管理',
      gestionImmoText: '放心将您的房产管理交给我们。我们的团队为您的房产资产提供严格且个性化的跟进。',
      gestionLocative: '全面租赁管理',
      rechercheLocataires: '租客搜索与筛选',
      suiviLoyers: '租金和费用跟踪',
      entretien: '物业维护保养',
      maitriseOeuvreTitle: '工程管理',
      maitriseOeuvreText: '我们从头到尾管理您的建设和翻新项目，确保质量、进度和预算。',
      conception: '设计和技术研究',
      coordination: '施工协调',
      suiviChantier: '工地跟进',
      reception: '验收和质量控制',
      schengenText: '申根区包含27个欧洲国家，允许自由通行。我们的申根签证协助服务包括：',
      analyseConseil: '情况分析和个性化建议',
      constitution: '完整材料准备',
      verification: '文件审核和优化',
      preparation: '为您获取与签证主管部门的预约',
      suivi: '申请进度跟踪',
      schengenCountries: '申根区国家',
      canadaText: '加拿大提供众多移民机会。我们为您提供各类签证协助：',
      visaTouriste: '旅游签证',
      visitCanada: '探访和发现加拿大',
      visaEtudiant: '学生签证',
      etudes: '加拿大大学留学',
      workvisa: '工作许可',
      workOpportunities: '职业发展机会',
      residence: '永久居留',
      immigration: '永久移民加拿大',
      process: '我们的流程',
      howWeWork: '我们的工作方式',
      consultation: '咨询',
      consultationDesc: '分析您的情况和移民目标',
      preparationStep: '准备',
      preparationStepDesc: '完整材料的准备和审核',
      submission: '提交',
      submissionDesc: '提交申请并向相关部门跟进',
      supportStep: '陪伴',
      supportStepDesc: '持续支持直至您与领事当局或其代表的会面',
      readyToStart: '准备开始您的项目？',
      contactForConsultation: '联系我们进行咨询，了解我们如何帮助您实现移民项目。',
      requestConsultation: '预约咨询',
      visaContactTitle: '签证联系方式',
      visaContactSubtitle: '联系我们的签证服务',
      visaPhone: 'WhatsApp 签证',
      whatsappOnly: '仅通过WhatsApp联系',
      visaEmail: '签证邮箱',
      visaEmailDesc: '所有签证相关咨询'
    },
    professionnels: {
      title: '咨询与培训',
      conseils: '咨询',
      formationsLink: '培训',
      subtitle: '商业策略、团队培训、光纤和电气资质咨询及工业卫生',
      services: '我们的服务',
      solutions: '为您的企业量身定制解决方案',
      strategie: '商业策略',
      strategieDesc: '通过量身定制的商业策略发展您的业务，适应您的市场和目标。',
      analyseMarche: '市场分析和定位',
      objectifs: '商业目标制定',
      planAction: '行动计划和实施',
      suiviKPI: '绩效指标跟踪',
      optimisation: '策略持续优化',
      formation: '团队培训',
      formationDesc: '加强您的销售团队能力，追求卓越。',
      techniquesVente: '高级销售技巧',
      negociation: '商业谈判',
      relationClient: '客户关系管理',
      leadership: '商业领导力',
      coaching: '个性化辅导',
      hygiene: '工业卫生与清洁',
      hygieneDesc: '确保您的工业流程在卫生和清洁方面符合规范和安全标准。',
      auditInstallations: '设施审计',
      conformite: '法规合规',
      plansSanitaires: '卫生管理计划',
      formationPersonnel: '人员培训',
      certification: '认证协助',
      clientMystere: '神秘顾客',
      clientMystereDesc: '通过我们的神秘访客评估您的客户服务质量和购物体验。',
      visitesMysteres: '实体店神秘访问',
      appelsMysteres: '神秘电话和联系',
      evaluationAccueil: '接待和咨询评估',
      rapportRecommandations: '详细报告和建议',
      suiviAmelioration: '改进计划跟进',
      marketing: '数字营销与创意',
      marketingDesc: '通过我们的数字解决方案提升您的在线影响力和品牌形象。',
      strategieDigital: '数字营销策略',
      creationSites: '网站和落地页创建',
      reseauxSociaux: '社交媒体管理',
      creationContenu: '内容创作和平面设计',
      campagnesPub: '在线广告活动',
      expertise: '我们的专长',
      accelerate: '加速企业增长',
      expertiseText1: 'Excelsium Conseils 在商业发展和合规方面为企业提供支持。我们个性化的方法和行业专业知识使我们能够为您提供适合您挑战的解决方案。',
      expertiseText2: '从制定商业策略到培训团队，再到工业卫生咨询，我们是您追求卓越的可靠合作伙伴。',
      entreprises: '服务企业数',
      satisfaction: '客户满意度',
      secteurs: '行业领域',
      results: '我们的成果',
      concreteResults: '为您的企业创造实际成果',
      resultsText: '我们以结果为导向的方法使我们能够为客户创造可衡量的价值。我们承诺明确的目标并陪伴其实现。',
      methodology: '成熟且灵活的方法论',
      expertsConsultants: '领域专家顾问',
      personalizedSupport: '个性化支持和持续跟进',
      croissance: '平均营收增长',
      auditsReussis: '审计通过率',
      collaborateurs: '已培训员工',
      recommandation: '推荐率',
      multiSector: '多行业专业能力',
      readyToDevelop: '准备发展您的业务？',
      contactForAnalysis: '联系我们进行需求分析，了解 Excelsium Conseils 如何推动您企业的增长。',
      requestQuote: '索取报价',
      seeDetails: '查看详情'
    },
    cm: {
      tagline: '神秘顾客',
      heroTitle: '神秘顾客服务，助您追求卓越',
      heroSubtitle: '通过我们的专业神秘访问，客观评估您的接待质量、客户服务和购物体验。',
      simpleProcess: '简单高效',
      howItWorks: '服务流程',
      step1Title: '准备',
      step1Desc: '我们共同定制您的神秘访问参数：时间表、评估问卷、神秘顾客的档案和场景，根据您的行业量身定制。',
      step2Title: '神秘访问',
      step2Desc: '我们的神秘顾客在现场和/或通过电话开展行动。他们像普通客户一样行事，评估体验的每个方面。',
      step3Title: '结果与分析',
      step3Desc: '每次神秘访问后您将收到详细报告。在线分析您的结果：排序、比较、筛选和导出数据以实现最佳跟踪。',
      objectivesTag: '为什么选择神秘顾客',
      objectivesTitle: '四大战略目标',
      obj1Title: '提升营业额',
      obj1Desc: '营收增长往往取决于人际互动的质量：追加销售、交叉销售、情感销售。神秘顾客能够揭示、测试和改进这些商业策略的实施。',
      obj2Title: '激励和表彰团队',
      obj2Desc: '神秘顾客报告突出的正面评价多于负面发现。评审有助于展示最佳实践并表彰表现出色的团队。',
      obj3Title: '提升在线声誉',
      obj3Desc: '在线评价（Google、Tripadvisor、Yelp）往往带有负面性但缺乏精确性。神秘顾客研究提供详细分析，以识别和纠正问题，改善客户体验。',
      obj4Title: '监控收银流程',
      obj4Desc: '现金和贵重物品的处理始终存在风险。专门针对收银的神秘顾客场景有助于保障这些流程并防止损失。',
      packTag: '完整方案',
      packTitle: '全包式神秘顾客套餐',
      packIntro: '我们的方案设计简单、全面、透明。每个套餐包含成功进行神秘顾客研究所需的全部服务。',
      packItem1: '根据您的行业和品牌定制研究（问卷、场景、干预方式）',
      packItem2: '按照您的时间表安排神秘访问',
      packItem3: '详细的访问报告',
      packItem4: '可选：神秘顾客进行购买或消费',
      packItem5: '可选：神秘顾客进行购买或消费',
      guaranteeTitle: '',
      guaranteeDesc: '',
      guaranteeText: '',
      ctaTitle: '准备好提升您的客户服务了吗？',
      ctaText: '了解我们适合各种需求和企业规模的定价方案。',
      seePricing: '查看价格'
    },
    cmTarifs: {
      heroTitle: '神秘顾客服务价格',
      heroSubtitle: '全包式方案，根据您的访问量调整。订购越多，每次访问的价格越低。',
      backToExplanation: '返回说明',
      pricingTag: '我们的方案',
      pricingTitle: '选择您的套餐',
      allInclusive: '全包套餐',
      eurPerVisit: '€ / 次',
      visit: '次',
      visitsMin: '次起订',
      feat1: '定制问卷',
      feat2: '现场或电话访问',
      feat3: '每次访问详细报告',
      feat4: '个性化支持',
      order: '立即订购',
      includedTag: '全部包含',
      includedTitle: '每个套餐包含的内容',
      inc1: '研究的完整定制（问卷、场景、档案）',
      inc2: '根据您的时间表灵活安排',
      inc3: '每次访问后的详细报告',
      inc4: '个性化支持',
      guarantee: '',
      ctaTitle: '需要定制报价？',
      ctaText: '如有特殊需求或大量订购，请直接联系我们获取定制方案。',
      contactUs: '联系我们',
      onlineDiscountTitle: '线上服务享受-30%优惠',
      onlineDiscountDesc: '神秘电话、电话评估和在线访问：相比实地服务价格享受30%折扣。'
    },
    conseils: { tagline: '我们的咨询', heroTitle: '企业战略咨询', heroSubtitle: '为您的企业发展提供量身定制的支持。', backToPro: '返回企业服务', seeOffer: '查看详细方案', ctaTitle: '需要个性化咨询？', ctaText: '联系我们讨论您的具体需求。', contactUs: '联系我们' },
    formations: { tagline: '我们的培训', heroTitle: '认证专业培训', heroSubtitle: '技术和资质培训，助力您和团队的技能提升。', backToPro: '返回企业服务', levels: '级别', program: '课程内容', aiprTitle: 'AIPR - 网络安全', aiprDuration: '1至2天', aiprDesc: '网络附近作业许可培训(AIPR)。', aiprLevel1: '设计师', aiprLevel2: '主管', aiprLevel3: '操作员', aiprDetail1: '法规和法律义务', aiprDetail2: 'AIPR考试准备', elecTitle: '低压电气资质', elecDuration: '1至3天', elecDesc: '低压电气资质认证培训。', elecLevel1: 'B0/H0/H0V', elecLevel2: 'BS/BE', elecLevel3: 'B1/B1V/B2/B2V', elecLevel4: 'BR/BC', elecDetail1: '了解电气风险', elecDetail2: '安全执行电气操作', elecDetail3: '低压干预', elecDetail4: '资格认证准备', telecomTitle: '电信 - 光纤', telecomDuration: '3个月', telecomDesc: '光纤技术全面培训。', telecomDetail1: '光纤图纸读取和制作', telecomDetail2: '架空、地下、室内和外墙电缆敷设', telecomDetail3: '熔接', telecomDetail4: 'OTDR测量和测试及光功率计', telecomDetail5: '用户连接', telecomDetail6: '专业设备使用', domotiqueTitle: '智能互联建筑技术员', domotiqueDuration: '6个月', domotiqueDesc: '建筑管理系统和智能家居培训。', domotiqueDetail1: '建筑管理系统(BMS)和集中技术管理(GTC)', domotiqueDetail2: '智能家居系统安装', domotiqueDetail3: '自动化：百叶窗、大门、智能照明', domotiqueDetail4: '传感器、报警和监控', domotiqueDetail5: '使用KNX协议配置物联网设备', domotiqueDetail6: '预防性和修复性维护', ctaTitle: '对培训感兴趣？', ctaText: '联系我们了解下一期课程和价格。', contactUs: '联系我们' },
    contact: {
      title: '联系我们',
      subtitle: '我们的团队随时为您解答所有问题',
      info: '联系信息',
      stayInTouch: '保持联系',
      infoText: '如有任何关于我们服务的问题，请随时与我们联系。我们将尽快回复您。',
      phone: '电话',
      whatsappNote: '如无法接通，请通过WhatsApp发送您的详细需求。',
      email: '邮箱',
      address: '地址',
      hours: '营业时间',
      weekdays: '周一至周五',
      saturday: '周六',
      sunday: '周日',
      byAppointment: '预约制',
      closed: '休息',
      form: '表单',
      sendMessage: '给我们留言',
      firstName: '名',
      lastName: '姓',
      yourFirstName: '您的名字',
      yourLastName: '您的姓氏',
      yourEmail: 'your@email.com',
      phonePlaceholder: '+33 6 12 34 56 78',
      service: '相关服务',
      selectService: '选择一项服务',
      message: '留言',
      messagePlaceholder: '描述您的项目或需求...',
      send: '发送消息',
      sending: '发送中...',
      success: '消息已发送！',
      successText: '感谢您联系我们。我们的团队将尽快回复您。',
      sendAnother: '发送另一条消息',
      services: {
        visaSchengen: '申根签证',
        visaCanada: '加拿大签证',
        gestionImmo: '房产管理',
        maitriseOeuvre: '工程管理',
        strategie: '商业策略',
        formation: '团队培训',
        hygiene: '工业卫生与清洁',
        clientMystere: '神秘顾客',
        marketing: '数字营销与创意',
        other: '其他需求'
      }
    },
    footer: {
      description: '您在签证办理和企业咨询方面的可靠合作伙伴。',
      particuliers: '个人服务',
      professionnels: '企业服务',
      contact: '联系方式',
      visaSchengen: '申根签证',
      visaCanada: '加拿大签证',
      accompagnement: '全程陪伴',
      gestionImmo: '房产管理',
      maitriseOeuvre: '工程管理',
      strategie: '商业策略',
      formations: '培训',
      hygiene: '工业卫生与清洁',
      clientMystere: '神秘顾客',
      marketing: '数字营销',
      rights: '版权所有。',
      legal: '法律声明',
      privacy: '隐私政策',
      taxNotice: '网站上显示的所有价格或我们代理人沟通的价格均为不含税价格。适用增值税率：19%。'
    }
  },
  ja: {
    nav: { home: 'ホーム', particuliers: '個人向け', professionnels: '法人向け', contact: 'お問い合わせ', contactUs: 'お問い合わせ' },
    home: { space: 'セクション', particuliers: '個人', professionnels: '法人', particuliersDesc: 'シェンゲンおよびカナダビザの相談・サポート', professionnelsDesc: '営業戦略、チーム研修、産業衛生コンサルティング', discover: '詳しく見る', about: '会社概要', aboutTitle: '卓越したサービスで成功をサポート', aboutText1: 'Excelsium Conseilsは移民プロジェクトおよびビジネス開発における信頼できるパートナーです。', aboutText2: 'ビザ手続きの個別サポートを求める個人の方も、業績の最適化を目指す企業の方も、私たちの専門知識をご活用ください。', yearsExp: '年の経験', whyUs: '選ばれる理由', commitment: '品質へのこだわり', expertise: '専門性', expertiseDesc: '各分野の専門コンサルタントによるチーム', support: 'サポート', supportDesc: '手続き全体を通じた個別フォロー', reactivity: '迅速な対応', reactivityDesc: '迅速な回答と効率的な書類処理', trust: '信頼', trustDesc: 'すべてのサービスにおいて透明性と機密性を保証', ready: '始める準備はできましたか？', together: '一緒にプロジェクトを実現しましょう', ctaText: '今すぐお問い合わせください。', ourServices: 'サービス一覧', teamBanner: 'フランス、カナダ、チュニジアの人材で構成された多文化で経験豊富なチームが、あなたの成功のためにサービスを提供します。', teamHighlight: 'Excelsium Conseils' },
    particuliers: { title: '個人向けサービス', subtitle: 'ビザ・移民、不動産管理、工事管理', services: 'サービス', complete: '総合的なサポート', visaSchengen: 'シェンゲンビザ', visaSchengenDesc: 'シェンゲン圏27カ国へのビザ申請サポート', visaCanada: 'カナダビザ', visaCanadaDesc: '観光、留学、就労、永住権ビザ', gestionImmo: '不動産管理', gestionImmoDesc: '賃貸管理と不動産資産の総合フォロー', maitriseOeuvre: '工事管理', maitriseOeuvreDesc: '建設・改修プロジェクトの全面管理', service: 'サービス', successRate: '成功率', ourSuccessRate: '当社の成功率', successRateValue: '+75%', successRateLabel: '承認された申請', vsCompetitors: '平均拒否率', competitorRate: '~40%', competitorRateLabel: '他のサービス提供者', successRateText: '当社の専門知識と個別サポートにより、市場平均を大きく上回る成功率を達成しています。', gestionImmoTitle: '不動産管理', gestionImmoText: '安心して不動産管理をお任せください。', gestionLocative: '総合賃貸管理', rechercheLocataires: 'テナントの検索と選定', suiviLoyers: '家賃・経費の追跡', entretien: '物件のメンテナンス', maitriseOeuvreTitle: '工事管理', maitriseOeuvreText: '品質、スケジュール、予算を保証しながらプロジェクトを管理します。', conception: '設計・技術調査', coordination: '工事の調整', suiviChantier: '現場フォロー', reception: '検収・品質管理', schengenText: 'シェンゲン圏は27のヨーロッパ諸国を含みます。当社のサポート：', analyseConseil: '状況分析と個別アドバイス', constitution: '完全な書類の準備', verification: '書類の確認と最適化', preparation: 'ビザ発給の管轄当局との面談予約の取得', suivi: '申請の進捗追跡', schengenCountries: 'シェンゲン圏の国々', canadaText: 'カナダは多くの移民機会を提供しています：', visaTouriste: '観光ビザ', visitCanada: 'カナダの訪問と発見', visaEtudiant: '学生ビザ', etudes: 'カナダの大学への留学', workvisa: '就労許可', workOpportunities: 'キャリアの機会', residence: '永住権', immigration: 'カナダへの永住', process: 'プロセス', howWeWork: '作業の流れ', consultation: '相談', consultationDesc: '状況と移民目標の分析', preparationStep: '準備', preparationStepDesc: '完全な書類の準備と確認', submission: '提出', submissionDesc: '申請の提出と当局へのフォロー', supportStep: 'サポート', supportStepDesc: '領事当局またはその代理人との面談まで継続的にサポート', readyToStart: 'プロジェクトを始める準備はできましたか？', contactForConsultation: 'お問い合わせください。', requestConsultation: '相談を予約', visaContactTitle: 'ビザの連絡先', visaContactSubtitle: 'ビザサービスへのお問い合わせ', visaPhone: 'WhatsApp ビザ', whatsappOnly: 'WhatsAppのみで連絡可能', visaEmail: 'ビザメール', visaEmailDesc: 'ビザに関するすべてのお問い合わせ' },
    professionnels: { title: 'コンサルティング＆研修', conseils: 'コンサルティング', formationsLink: '研修', subtitle: '営業戦略、チーム研修、光ファイバー・電気資格コンサルティング、産業衛生', services: 'サービス', solutions: '御社に合わせたカスタムソリューション', strategie: '営業戦略', strategieDesc: 'オーダーメイドの営業戦略で事業を発展させましょう。', analyseMarche: '市場分析とポジショニング', objectifs: '営業目標の設定', planAction: 'アクションプランと実行', suiviKPI: 'パフォーマンス指標の追跡', optimisation: '戦略の継続的最適化', formation: 'チーム研修', formationDesc: '営業チームのスキルを強化しましょう。', techniquesVente: '高度な販売テクニック', negociation: '商談スキル', relationClient: '顧客関係管理', leadership: '営業リーダーシップ', coaching: '個別コーチング', hygiene: '産業衛生・清掃', hygieneDesc: '工業プロセスの適合性と安全性を確保します。', auditInstallations: '施設監査', conformite: '規制適合', plansSanitaires: '衛生管理計画', formationPersonnel: 'スタッフ研修', certification: '認証サポート', clientMystere: 'ミステリーショッパー', clientMystereDesc: 'ミステリー訪問を通じて顧客サービスの品質を評価します。', visitesMysteres: '店舗でのミステリー訪問', appelsMysteres: 'ミステリーコール', evaluationAccueil: '接客とアドバイスの評価', rapportRecommandations: '詳細レポートと提言', suiviAmelioration: '改善計画のフォロー', marketing: 'デジタルマーケティング', marketingDesc: 'デジタルソリューションでオンラインプレゼンスを向上させましょう。', strategieDigital: 'デジタルマーケティング戦略', creationSites: 'ウェブサイト制作', reseauxSociaux: 'SNS管理', creationContenu: 'コンテンツ制作・デザイン', campagnesPub: 'オンライン広告', expertise: '専門性', accelerate: '企業成長を加速', expertiseText1: 'Excelsium Conseilsは事業開発とコンプライアンスにおいて企業をサポートします。', expertiseText2: '営業戦略からチーム研修まで、卓越性を追求する信頼できるパートナーです。', entreprises: 'サポート企業数', satisfaction: '顧客満足度', secteurs: '業種', results: '実績', concreteResults: '御社に具体的な成果を', resultsText: '結果重視のアプローチにより測定可能な価値を生み出します。', methodology: '実績ある柔軟な方法論', expertsConsultants: '専門コンサルタント', personalizedSupport: '個別サポートと継続的フォロー', croissance: '平均売上成長率', auditsReussis: '監査合格率', collaborateurs: '研修済みスタッフ', recommandation: '推薦率', multiSector: '多業種の専門性', readyToDevelop: '事業を発展させる準備はできましたか？', contactForAnalysis: 'ニーズ分析にお問い合わせください。', requestQuote: '見積もりを依頼', seeDetails: '詳細を見る' },
    cm: { tagline: 'ミステリーショッパー', heroTitle: '卓越性のためのミステリーショッパーサービス', heroSubtitle: '専門的なミステリー訪問を通じて接客品質を客観的に評価します。', simpleProcess: 'シンプルで効果的', howItWorks: 'サービスの流れ', step1Title: '準備', step1Desc: 'ミステリー訪問のパラメーターを一緒にカスタマイズします。', step2Title: 'ミステリー訪問', step2Desc: '現場および/または電話でミステリーショッパーが活動を開始します。', step3Title: '結果と分析', step3Desc: '各ミステリー訪問後に詳細なレポートを受け取れます。', objectivesTag: 'ミステリーショッパーを選ぶ理由', objectivesTitle: '4つの戦略目標', obj1Title: '売上の向上', obj1Desc: 'ミステリーショッパーは営業戦略の実践を明らかにし改善します。', obj2Title: 'チームの動機付け', obj2Desc: 'レポートは肯定的な点を多く浮き彫りにし優秀なチームを表彰します。', obj3Title: 'オンライン評判の向上', obj3Desc: '詳細な分析で問題を特定し修正します。', obj4Title: 'レジ管理の監視', obj4Desc: 'レジに特化したシナリオでプロセスを安全にします。', packTag: '完全パッケージ', packTitle: 'オールインクルーシブパック', packIntro: 'シンプル、完全、透明な設計です。', packItem1: '業種に合わせた調査のカスタマイズ', packItem2: 'スケジュールに合わせた計画的なミステリー訪問', packItem3: '詳細な訪問レポート', packItem4: 'オプション：ミステリーショッパーによる購入', packItem5: 'オプション：ミステリーショッパーによる購入', guaranteeTitle: '', guaranteeDesc: '', guaranteeText: '', ctaTitle: '顧客サービスを改善する準備はできましたか？', ctaText: 'あらゆるニーズに対応した料金プランをご覧ください。', seePricing: '料金を見る' },
    cmTarifs: { heroTitle: 'ミステリーショッパー料金', heroSubtitle: '訪問回数に応じたオールインクルーシブプラン。', backToExplanation: '説明に戻る', pricingTag: 'プラン', pricingTitle: 'パックを選択', allInclusive: 'オールインクルーシブ', eurPerVisit: '€ / 回', visit: '回', visitsMin: '回以上', feat1: 'カスタムアンケート', feat2: '現場または電話訪問', feat3: '訪問ごとの詳細レポート', feat4: '個別サポート', order: '注文する', includedTag: 'すべて含まれています', includedTitle: '各パックの内容', inc1: '調査の完全カスタマイズ', inc2: 'スケジュールに合わせた柔軟な計画', inc3: '各訪問後の詳細レポート', inc4: '個別サポート', guarantee: '', ctaTitle: 'カスタム見積もりが必要ですか？', ctaText: '特別なニーズの場合は直接お問い合わせください。', contactUs: 'お問い合わせ', onlineDiscountTitle: 'オンラインサービス-30%割引', onlineDiscountDesc: 'ミステリーコール、電話評価、オンライン訪問：現場サービスと比べて30%の割引。' },
    conseils: { tagline: 'コンサルティング', heroTitle: '企業向け戦略コンサルティング', heroSubtitle: '事業発展のためのオーダーメイドサポート。', backToPro: '法人サービスに戻る', seeOffer: '詳細を見る', ctaTitle: '個別のアドバイスが必要ですか？', ctaText: 'お気軽にお問い合わせください。', contactUs: 'お問い合わせ' },
    formations: { tagline: '研修', heroTitle: '認定プロフェッショナル研修', heroSubtitle: 'スキルアップのための技術・資格研修。', backToPro: '法人サービスに戻る', levels: 'レベル', program: 'プログラム', aiprTitle: 'AIPR - ネットワーク安全', aiprDuration: '1〜2日', aiprDesc: 'ネットワーク付近作業許可研修。', aiprLevel1: '設計者', aiprLevel2: '監督者', aiprLevel3: 'オペレーター', aiprDetail1: '規制と法的義務', aiprDetail2: 'AIPR試験準備', elecTitle: '低圧電気資格', elecDuration: '1〜3日', elecDesc: '低圧電気資格研修。', elecLevel1: 'B0/H0/H0V', elecLevel2: 'BS/BE', elecLevel3: 'B1/B1V/B2/B2V', elecLevel4: 'BR/BC', elecDetail1: '電気リスクの理解', elecDetail2: '電気作業の安全実施', elecDetail3: '低圧介入', elecDetail4: '資格認定準備', telecomTitle: 'テレコム - 光ファイバー', telecomDuration: '3ヶ月', telecomDesc: '光ファイバー技術の総合研修。', telecomDetail1: '光ファイバー図面の読解と作成', telecomDetail2: '架空、地下、屋内、外壁ケーブル敷設', telecomDetail3: '融着接続', telecomDetail4: 'OTDRとパワーメーターによる測定とテスト', telecomDetail5: '加入者接続', telecomDetail6: '専門機器の使用', domotiqueTitle: 'コミュニケーティング・コネクテッドビルディング技術者', domotiqueDuration: '6ヶ月', domotiqueDesc: 'ビル管理システムとスマートホーム研修。', domotiqueDetail1: 'ビル管理システム(BMS)と集中技術管理(GTC)', domotiqueDetail2: 'スマートホームシステムの設置', domotiqueDetail3: '自動化：シャッター、ゲート、スマート照明', domotiqueDetail4: 'センサー、アラーム、監視', domotiqueDetail5: 'KNXプロトコルを使用したIoTデバイス設定', domotiqueDetail6: '予防・修正メンテナンス', ctaTitle: '研修に興味がありますか？', ctaText: '次回セッションと料金についてお問い合わせください。', contactUs: 'お問い合わせ' },
    contact: { title: 'お問い合わせ', subtitle: '私たちのチームがすべてのご質問にお答えします', info: '連絡先情報', stayInTouch: 'ご連絡ください', infoText: 'サービスに関するご質問がございましたら、お気軽にお問い合わせください。', phone: '電話', whatsappNote: 'つながらない場合はWhatsAppで詳細をお送りください。', email: 'メール', address: '住所', hours: '営業時間', weekdays: '月曜〜金曜', saturday: '土曜', sunday: '日曜', byAppointment: '予約制', closed: '休業', form: 'フォーム', sendMessage: 'メッセージを送信', firstName: '名', lastName: '姓', yourFirstName: 'お名前', yourLastName: 'お名前（姓）', yourEmail: 'your@email.com', phonePlaceholder: '+33 6 12 34 56 78', service: '関連サービス', selectService: 'サービスを選択', message: 'メッセージ', messagePlaceholder: 'プロジェクトやご要望を記入してください...', send: 'メッセージを送信', sending: '送信中...', success: 'メッセージ送信完了！', successText: 'お問い合わせありがとうございます。', sendAnother: '別のメッセージを送信', services: { visaSchengen: 'シェンゲンビザ', visaCanada: 'カナダビザ', gestionImmo: '不動産管理', maitriseOeuvre: '工事管理', strategie: '営業戦略', formation: 'チーム研修', hygiene: '産業衛生・清掃', clientMystere: 'ミステリーショッパー', marketing: 'デジタルマーケティング', other: 'その他のご要望' } },
    footer: { description: 'ビザ手続きとビジネスコンサルティングの信頼できるパートナー。', particuliers: '個人向け', professionnels: '法人向け', contact: 'お問い合わせ', visaSchengen: 'シェンゲンビザ', visaCanada: 'カナダビザ', accompagnement: 'サポート', gestionImmo: '不動産管理', maitriseOeuvre: '工事管理', strategie: '営業戦略', formations: '研修', hygiene: '産業衛生・清掃', clientMystere: 'ミステリーショッパー', marketing: 'デジタルマーケティング', rights: 'All rights reserved.', legal: '法的通知', privacy: 'プライバシーポリシー', taxNotice: 'サイトに表示されているすべての価格は税抜き（HT）です。適用消費税率：19%。' }
  },
  es: {
    nav: { home: 'Inicio', particuliers: 'Particulares', professionnels: 'Profesionales', contact: 'Contacto', contactUs: 'Contáctenos' },
    home: { space: 'Espacio', particuliers: 'Particulares', professionnels: 'Profesionales', particuliersDesc: 'Asesoría y acompañamiento para sus trámites de visa Schengen y Canadá', professionnelsDesc: 'Estrategia comercial, formación y consultoría en higiene industrial', discover: 'Descubrir', about: 'Sobre Nosotros', aboutTitle: 'La Excelencia al Servicio de Su Éxito', aboutText1: 'Excelsium Conseils es su socio de confianza para todos sus proyectos de inmigración y desarrollo empresarial.', aboutText2: 'Ya sea un particular o una empresa, ponemos nuestro conocimiento a su servicio.', yearsExp: 'Años de Experiencia', whyUs: 'Por Qué Elegirnos', commitment: 'Nuestro Compromiso de Calidad', expertise: 'Experiencia', expertiseDesc: 'Un equipo de consultores expertos en sus respectivos campos', support: 'Acompañamiento', supportDesc: 'Seguimiento personalizado durante todos sus trámites', reactivity: 'Rapidez', reactivityDesc: 'Respuestas rápidas y procesamiento eficiente', trust: 'Confianza', trustDesc: 'Transparencia y confidencialidad garantizadas', ready: '¿Listo para Comenzar?', together: 'Hagamos Realidad Sus Proyectos Juntos', ctaText: 'Contáctenos hoy para una consulta.', ourServices: 'Nuestros Servicios', teamBanner: 'Un equipo multicultural y experimentado, compuesto por talentos franceses, canadienses y tunecinos, al servicio de su éxito.', teamHighlight: 'Excelsium Conseils' },
    particuliers: { title: 'Servicios para Particulares', subtitle: 'Visa e Inmigración, Gestión Inmobiliaria y Dirección de Obra', services: 'Nuestros Servicios', complete: 'Un Acompañamiento Completo', visaSchengen: 'Visa Schengen', visaSchengenDesc: 'Acompañamiento para sus solicitudes de visa a los 27 países Schengen', visaCanada: 'Visa Canadá', visaCanadaDesc: 'Turista, estudiante, trabajo o residencia permanente', gestionImmo: 'Gestión Inmobiliaria', gestionImmoDesc: 'Gestión completa de alquileres y patrimonio inmobiliario', maitriseOeuvre: 'Dirección de Obra', maitriseOeuvreDesc: 'Gestión integral de proyectos de construcción y renovación', service: 'Servicio', successRate: 'Tasa de Éxito', ourSuccessRate: 'Nuestra Tasa de Éxito', successRateValue: '+75%', successRateLabel: 'de expedientes aprobados', vsCompetitors: 'Tasa media de rechazo', competitorRate: '~40%', competitorRateLabel: 'en otros proveedores', successRateText: 'Gracias a nuestra experiencia, obtenemos una tasa de éxito muy superior a la media del mercado.', gestionImmoTitle: 'Gestión Inmobiliaria', gestionImmoText: 'Confíenos la gestión de sus bienes inmobiliarios con total tranquilidad.', gestionLocative: 'Gestión completa de alquileres', rechercheLocataires: 'Búsqueda y selección de inquilinos', suiviLoyers: 'Seguimiento de alquileres y gastos', entretien: 'Mantenimiento de propiedades', maitriseOeuvreTitle: 'Dirección de Obra', maitriseOeuvreText: 'Gestionamos sus proyectos de principio a fin.', conception: 'Diseño y estudios técnicos', coordination: 'Coordinación de obras', suiviChantier: 'Seguimiento de obra', reception: 'Recepción y control de calidad', schengenText: 'El espacio Schengen agrupa 27 países europeos. Nuestro servicio incluye:', analyseConseil: 'Análisis de su situación y asesoramiento personalizado', constitution: 'Preparación del expediente completo', verification: 'Verificación y optimización de documentos', preparation: 'Obtención de su cita con las autoridades competentes para la obtención del visado', suivi: 'Seguimiento de su solicitud', schengenCountries: 'Países del Espacio Schengen', canadaText: 'Canadá ofrece numerosas oportunidades de inmigración:', visaTouriste: 'Visa de Turista', visitCanada: 'Visita y descubrimiento de Canadá', visaEtudiant: 'Visa de Estudiante', etudes: 'Estudios en universidades canadienses', workvisa: 'Permiso de Trabajo', workOpportunities: 'Oportunidades profesionales', residence: 'Residencia Permanente', immigration: 'Inmigración definitiva a Canadá', process: 'Nuestro Proceso', howWeWork: 'Cómo Trabajamos', consultation: 'Consulta', consultationDesc: 'Análisis de su situación', preparationStep: 'Preparación', preparationStepDesc: 'Constitución y verificación del expediente', submission: 'Presentación', submissionDesc: 'Presentación de su solicitud y seguimiento', supportStep: 'Acompañamiento', supportStepDesc: 'Apoyo continuo hasta su cita con las autoridades consulares o sus representantes', readyToStart: '¿Listo para Iniciar Su Proyecto?', contactForConsultation: 'Contáctenos para una consulta.', requestConsultation: 'Solicitar una Consulta', visaContactTitle: 'Contacto Visa', visaContactSubtitle: 'Contacte Nuestro Servicio de Visas', visaPhone: 'WhatsApp Visa', whatsappOnly: 'Solo disponible por WhatsApp', visaEmail: 'Email Visa', visaEmailDesc: 'Para todas las consultas sobre visas' },
    professionnels: { title: 'Consultoría y Formación', conseils: 'Consultoría', formationsLink: 'Formaciones', subtitle: 'Estrategia comercial, formación de equipos, consultoría en fibra y habilitación eléctrica e higiene industrial', services: 'Nuestros Servicios', solutions: 'Soluciones a Medida para Su Empresa', strategie: 'Estrategia Comercial', strategieDesc: 'Desarrolle su actividad con una estrategia comercial a medida.', analyseMarche: 'Análisis de mercado y posicionamiento', objectifs: 'Definición de objetivos comerciales', planAction: 'Plan de acción e implementación', suiviKPI: 'Seguimiento de indicadores de rendimiento', optimisation: 'Optimización continua', formation: 'Formación de Equipos', formationDesc: 'Refuerce las competencias de sus equipos comerciales.', techniquesVente: 'Técnicas avanzadas de venta', negociation: 'Negociación comercial', relationClient: 'Gestión de la relación con el cliente', leadership: 'Liderazgo comercial', coaching: 'Coaching individualizado', hygiene: 'Higiene y Limpieza Industrial', hygieneDesc: 'Garantice la conformidad y seguridad de sus procesos industriales.', auditInstallations: 'Auditoría de instalaciones', conformite: 'Cumplimiento normativo', plansSanitaires: 'Planes de gestión sanitaria', formationPersonnel: 'Formación del personal', certification: 'Acompañamiento en certificación', clientMystere: 'Cliente Misterioso', clientMystereDesc: 'Evalúe la calidad de su servicio al cliente mediante nuestras visitas misteriosas.', visitesMysteres: 'Visitas misteriosas en punto de venta', appelsMysteres: 'Llamadas y contactos misteriosos', evaluationAccueil: 'Evaluación de la recepción', rapportRecommandations: 'Informe detallado con recomendaciones', suiviAmelioration: 'Seguimiento de planes de mejora', marketing: 'Marketing y Creación Digital', marketingDesc: 'Impulse su presencia en línea con nuestras soluciones digitales.', strategieDigital: 'Estrategia de marketing digital', creationSites: 'Creación de sitios web', reseauxSociaux: 'Gestión de redes sociales', creationContenu: 'Creación de contenido y diseño gráfico', campagnesPub: 'Campañas publicitarias en línea', expertise: 'Nuestra Experiencia', accelerate: 'Acelere el Crecimiento de Su Empresa', expertiseText1: 'Excelsium Conseils acompaña a las empresas en su desarrollo comercial.', expertiseText2: 'Desde la estrategia hasta la formación, somos su socio de confianza.', entreprises: 'Empresas Acompañadas', satisfaction: 'Satisfacción del Cliente', secteurs: 'Sectores de Actividad', results: 'Nuestros Resultados', concreteResults: 'Resultados Concretos para Su Empresa', resultsText: 'Nuestro enfoque orientado a resultados genera valor medible.', methodology: 'Metodología probada y adaptable', expertsConsultants: 'Consultores expertos', personalizedSupport: 'Acompañamiento personalizado', croissance: 'Crecimiento medio de facturación', auditsReussis: 'Auditorías exitosas', collaborateurs: 'Colaboradores formados', recommandation: 'Tasa de recomendación', multiSector: 'Experiencia Multisectorial', readyToDevelop: '¿Listo para Desarrollar Su Actividad?', contactForAnalysis: 'Contáctenos para un análisis de sus necesidades.', requestQuote: 'Solicitar un Presupuesto', seeDetails: 'Ver ofertas' },
    cm: { tagline: 'Cliente Misterioso', heroTitle: 'El Cliente Misterioso al Servicio de Su Excelencia', heroSubtitle: 'Evalúe objetivamente la calidad de su recepción y servicio al cliente.', simpleProcess: 'Simple y Eficaz', howItWorks: 'Cómo Funciona', step1Title: 'Preparación', step1Desc: 'Personalizamos juntos los parámetros de sus visitas misteriosas.', step2Title: 'Visitas Misteriosas', step2Desc: 'Nuestros clientes misteriosos evalúan cada aspecto de la experiencia.', step3Title: 'Resultados y Análisis', step3Desc: 'Recibe un informe detallado después de cada visita misteriosa.', objectivesTag: 'Por Qué el Cliente Misterioso', objectivesTitle: 'Cuatro Objetivos Estratégicos', obj1Title: 'Aumentar la Facturación', obj1Desc: 'El cliente misterioso permite revelar y mejorar las estrategias comerciales.', obj2Title: 'Motivar a los Equipos', obj2Desc: 'Un informe destaca más puntos positivos que negativos.', obj3Title: 'Mejorar la Reputación en Línea', obj3Desc: 'Análisis detallado para identificar y corregir disfunciones.', obj4Title: 'Controlar los Cobros', obj4Desc: 'Escenario específico para asegurar los procesos de cobro.', packTag: 'Oferta Completa', packTitle: 'El Pack Cliente Misterioso Todo Incluido', packIntro: 'Oferta simple, completa y sin sorpresas.', packItem1: 'Personalización del estudio', packItem2: 'Visitas misteriosas planificadas', packItem3: 'Informes detallados', packItem4: 'Opcional: compra por el cliente misterioso', packItem5: 'Opcional: compra por el cliente misterioso', guaranteeTitle: '', guaranteeDesc: '', guaranteeText: '', ctaTitle: '¿Listo para Mejorar Su Servicio al Cliente?', ctaText: 'Descubra nuestras tarifas adaptadas a todas las necesidades.', seePricing: 'Ver Tarifas' },
    cmTarifs: { heroTitle: 'Tarifas de Cliente Misterioso', heroSubtitle: 'Planes todo incluido adaptados a su volumen de visitas.', backToExplanation: 'Volver a las explicaciones', pricingTag: 'Nuestros Planes', pricingTitle: 'Elija Su Pack', allInclusive: 'Pack todo incluido', eurPerVisit: '€ / visita', visit: 'visita', visitsMin: 'visitas mínimo', feat1: 'Cuestionario personalizado', feat2: 'Visitas presenciales o telefónicas', feat3: 'Informe detallado por visita', feat4: 'Acompañamiento personalizado', order: 'Pedir', includedTag: 'Todo Incluido', includedTitle: 'Qué Incluye Cada Pack', inc1: 'Personalización completa del estudio', inc2: 'Planificación flexible', inc3: 'Informes detallados después de cada visita', inc4: 'Acompañamiento personalizado', guarantee: '', ctaTitle: '¿Necesita un Presupuesto Personalizado?', ctaText: 'Para necesidades específicas, contáctenos directamente.', contactUs: 'Contáctenos', onlineDiscountTitle: '-30% en todas las intervenciones en línea', onlineDiscountDesc: 'Llamadas misteriosas, evaluaciones telefónicas y visitas en línea: disfrute de un 30% de descuento.' },
    conseils: { tagline: 'Consultoría', heroTitle: 'Consultoría Estratégica para Su Empresa', heroSubtitle: 'Acompañamiento a medida para desarrollar su actividad.', backToPro: 'Volver a servicios profesionales', seeOffer: 'Ver oferta detallada', ctaTitle: '¿Necesita Asesoramiento Personalizado?', ctaText: 'Contáctenos para discutir sus necesidades.', contactUs: 'Contáctenos' },
    formations: { tagline: 'Formaciones', heroTitle: 'Formaciones Profesionales Certificadas', heroSubtitle: 'Formaciones técnicas y cualificantes para el desarrollo de competencias.', backToPro: 'Volver a servicios profesionales', levels: 'Niveles', program: 'Programa', aiprTitle: 'AIPR - Redes', aiprDuration: '1 a 2 días', aiprDesc: 'Formación en autorización de trabajo cerca de redes.', aiprLevel1: 'Diseñador', aiprLevel2: 'Supervisor', aiprLevel3: 'Operador', aiprDetail1: 'Normativas y obligaciones legales', aiprDetail2: 'Preparación del examen AIPR', elecTitle: 'Habilitaciones Eléctricas BT', elecDuration: '1 a 3 días', elecDesc: 'Formación en habilitaciones eléctricas de baja tensión.', elecLevel1: 'B0/H0/H0V', elecLevel2: 'BS/BE', elecLevel3: 'B1/B1V/B2/B2V', elecLevel4: 'BR/BC', elecDetail1: 'Conocer los riesgos eléctricos', elecDetail2: 'Realizar operaciones eléctricas con seguridad', elecDetail3: 'Intervenciones en baja tensión', elecDetail4: 'Preparación para la certificación', telecomTitle: 'Telecom - Fibra Óptica', telecomDuration: '3 meses', telecomDesc: 'Formación completa en técnicas de fibra óptica.', telecomDetail1: 'Lectura y creación de planos de fibra', telecomDetail2: 'Tendido de cables aéreo, subterráneo, interior y en fachada', telecomDetail3: 'Empalme por fusión', telecomDetail4: 'Mediciones y pruebas OTDR y fotómetro', telecomDetail5: 'Conexión de abonados', telecomDetail6: 'Uso de equipos profesionales', domotiqueTitle: 'Técnico en Edificios Comunicantes y Conectados', domotiqueDuration: '6 meses', domotiqueDesc: 'Formación en gestión técnica de edificios y domótica.', domotiqueDetail1: 'Gestión técnica de edificios (GTB) y gestión técnica centralizada (GTC)', domotiqueDetail2: 'Instalación de sistemas domóticos', domotiqueDetail3: 'Automatización: persianas, puertas, iluminación inteligente', domotiqueDetail4: 'Sensores, alarma y videovigilancia', domotiqueDetail5: 'Configuración de objetos conectados mediante protocolo KNX', domotiqueDetail6: 'Mantenimiento preventivo y correctivo', ctaTitle: '¿Interesado en una Formación?', ctaText: 'Contáctenos para conocer las próximas sesiones y tarifas.', contactUs: 'Contáctenos' },
    contact: { title: 'Contáctenos', subtitle: 'Nuestro equipo está a su disposición', info: 'Información', stayInTouch: 'Mantengamos el Contacto', infoText: 'No dude en contactarnos para cualquier pregunta.', phone: 'Teléfono', whatsappNote: 'Si no estamos disponibles, envíe un mensaje por WhatsApp.', email: 'Email', address: 'Dirección', hours: 'Horario de Apertura', weekdays: 'Lunes - Viernes', saturday: 'Sábado', sunday: 'Domingo', byAppointment: 'Con cita previa', closed: 'Cerrado', form: 'Formulario', sendMessage: 'Envíenos un Mensaje', firstName: 'Nombre', lastName: 'Apellido', yourFirstName: 'Su nombre', yourLastName: 'Su apellido', yourEmail: 'su@email.com', phonePlaceholder: '+33 6 12 34 56 78', service: 'Servicio relacionado', selectService: 'Seleccione un servicio', message: 'Mensaje', messagePlaceholder: 'Describa su proyecto o solicitud...', send: 'Enviar el Mensaje', sending: 'Enviando...', success: '¡Mensaje Enviado!', successText: 'Gracias por contactarnos. Le responderemos lo antes posible.', sendAnother: 'Enviar otro mensaje', services: { visaSchengen: 'Visa Schengen', visaCanada: 'Visa Canadá', gestionImmo: 'Gestión Inmobiliaria', maitriseOeuvre: 'Dirección de Obra', strategie: 'Estrategia Comercial', formation: 'Formación de Equipos', hygiene: 'Higiene y Limpieza Industrial', clientMystere: 'Cliente Misterioso', marketing: 'Marketing Digital', other: 'Otra solicitud' } },
    footer: { description: 'Su socio de confianza para trámites de visa y consultoría empresarial.', particuliers: 'Particulares', professionnels: 'Profesionales', contact: 'Contacto', visaSchengen: 'Visa Schengen', visaCanada: 'Visa Canadá', accompagnement: 'Acompañamiento', gestionImmo: 'Gestión Inmobiliaria', maitriseOeuvre: 'Dirección de Obra', strategie: 'Estrategia Comercial', formations: 'Formaciones', hygiene: 'Higiene y Limpieza Industrial', clientMystere: 'Cliente Misterioso', marketing: 'Marketing Digital', rights: 'Todos los derechos reservados.', legal: 'Aviso Legal', privacy: 'Política de Privacidad', taxNotice: 'Todos los precios mostrados son sin impuestos (IVA excluido). IVA aplicable: 19%.' }
  },
  it: {
    nav: { home: 'Home', particuliers: 'Privati', professionnels: 'Professionisti', contact: 'Contatto', contactUs: 'Contattaci' },
    home: { space: 'Sezione', particuliers: 'Privati', professionnels: 'Professionisti', particuliersDesc: 'Consulenza e assistenza per le pratiche di visto Schengen e Canada', professionnelsDesc: 'Strategia commerciale, formazione e consulenza in igiene industriale', discover: 'Scopri', about: 'Chi Siamo', aboutTitle: "L'Eccellenza al Servizio del Vostro Successo", aboutText1: "Excelsium Conseils è il vostro partner di fiducia per tutti i vostri progetti di immigrazione e sviluppo aziendale.", aboutText2: "Che siate privati o un'azienda, mettiamo il nostro know-how al vostro servizio.", yearsExp: "Anni di Esperienza", whyUs: 'Perché Sceglierci', commitment: 'Il Nostro Impegno di Qualità', expertise: 'Competenza', expertiseDesc: 'Un team di consulenti esperti nei rispettivi campi', support: 'Accompagnamento', supportDesc: 'Follow-up personalizzato durante tutte le pratiche', reactivity: 'Reattività', reactivityDesc: 'Risposte rapide e trattamento efficiente', trust: 'Fiducia', trustDesc: 'Trasparenza e riservatezza garantite', ready: 'Pronti a Iniziare?', together: 'Realizziamo Insieme i Vostri Progetti', ctaText: 'Contattateci oggi per una consulenza.', ourServices: 'I Nostri Servizi', teamBanner: 'Un team multiculturale e esperto, composto da talenti francesi, canadesi e tunisini, al servizio del vostro successo.', teamHighlight: 'Excelsium Conseils' },
    particuliers: { title: 'Servizi per Privati', subtitle: 'Visto e Immigrazione, Gestione Immobiliare e Direzione Lavori', services: 'I Nostri Servizi', complete: 'Un Accompagnamento Completo', visaSchengen: 'Visto Schengen', visaSchengenDesc: 'Assistenza per le domande di visto per i 27 paesi Schengen', visaCanada: 'Visto Canada', visaCanadaDesc: 'Turista, studente, lavoro o residenza permanente', gestionImmo: 'Gestione Immobiliare', gestionImmoDesc: 'Gestione locativa completa e monitoraggio del patrimonio', maitriseOeuvre: 'Direzione Lavori', maitriseOeuvreDesc: 'Gestione integrale dei progetti di costruzione e ristrutturazione', service: 'Servizio', successRate: 'Tasso di Successo', ourSuccessRate: 'Il Nostro Tasso di Successo', successRateValue: '+75%', successRateLabel: 'di pratiche approvate', vsCompetitors: 'Tasso medio di rifiuto', competitorRate: '~40%', competitorRateLabel: 'presso altri fornitori', successRateText: 'Grazie alla nostra competenza, otteniamo un tasso di successo nettamente superiore alla media.', gestionImmoTitle: 'Gestione Immobiliare', gestionImmoText: 'Affidateci la gestione dei vostri beni immobiliari in tutta tranquillità.', gestionLocative: 'Gestione locativa completa', rechercheLocataires: 'Ricerca e selezione di inquilini', suiviLoyers: 'Monitoraggio affitti e spese', entretien: 'Manutenzione degli immobili', maitriseOeuvreTitle: 'Direzione Lavori', maitriseOeuvreText: 'Gestiamo i vostri progetti dalla A alla Z.', conception: 'Progettazione e studi tecnici', coordination: 'Coordinamento dei lavori', suiviChantier: 'Monitoraggio del cantiere', reception: 'Consegna e controllo qualità', schengenText: 'Lo spazio Schengen raggruppa 27 paesi europei. Il nostro servizio include:', analyseConseil: 'Analisi della situazione e consulenza personalizzata', constitution: 'Preparazione del fascicolo completo', verification: 'Verifica e ottimizzazione dei documenti', preparation: "Ottenimento dell'appuntamento con le autorità competenti per il rilascio del visto", suivi: 'Monitoraggio della domanda', schengenCountries: 'Paesi dello Spazio Schengen', canadaText: 'Il Canada offre numerose opportunità di immigrazione:', visaTouriste: 'Visto Turistico', visitCanada: 'Visita e scoperta del Canada', visaEtudiant: 'Visto Studente', etudes: 'Studi nelle università canadesi', workvisa: 'Permesso di Lavoro', workOpportunities: 'Opportunità professionali', residence: 'Residenza Permanente', immigration: 'Immigrazione definitiva in Canada', process: 'Il Nostro Processo', howWeWork: 'Come Lavoriamo', consultation: 'Consulenza', consultationDesc: 'Analisi della situazione', preparationStep: 'Preparazione', preparationStepDesc: 'Preparazione e verifica del fascicolo', submission: 'Presentazione', submissionDesc: 'Presentazione della domanda e follow-up', supportStep: 'Accompagnamento', supportStepDesc: "Supporto continuo fino al vostro appuntamento con le autorità consolari o i loro rappresentanti", readyToStart: 'Pronti a Lanciare il Vostro Progetto?', contactForConsultation: 'Contattateci per una consulenza.', requestConsultation: 'Richiedere una Consulenza', visaContactTitle: 'Contatto Visto', visaContactSubtitle: 'Contattate il Nostro Servizio Visti', visaPhone: 'WhatsApp Visto', whatsappOnly: 'Raggiungibile solo via WhatsApp', visaEmail: 'Email Visto', visaEmailDesc: 'Per tutte le richieste relative ai visti' },
    professionnels: { title: 'Consulenza e Formazione', conseils: 'Consulenza', formationsLink: 'Formazione', subtitle: 'Strategia commerciale, formazione dei team, consulenza in fibra e abilitazione elettrica e igiene industriale', services: 'I Nostri Servizi', solutions: 'Soluzioni Su Misura per la Vostra Azienda', strategie: 'Strategia Commerciale', strategieDesc: 'Sviluppate la vostra attività con una strategia su misura.', analyseMarche: 'Analisi di mercato e posizionamento', objectifs: 'Definizione degli obiettivi commerciali', planAction: "Piano d'azione e implementazione", suiviKPI: 'Monitoraggio degli indicatori di performance', optimisation: 'Ottimizzazione continua', formation: 'Formazione dei Team', formationDesc: 'Rafforzate le competenze dei vostri team commerciali.', techniquesVente: 'Tecniche di vendita avanzate', negociation: 'Negoziazione commerciale', relationClient: 'Gestione della relazione con il cliente', leadership: 'Leadership commerciale', coaching: 'Coaching individualizzato', hygiene: 'Igiene e Pulizia Industriale', hygieneDesc: 'Garantite la conformità e la sicurezza dei vostri processi.', auditInstallations: 'Audit delle installazioni', conformite: 'Conformità normativa', plansSanitaires: 'Piani di gestione sanitaria', formationPersonnel: 'Formazione del personale', certification: 'Accompagnamento alla certificazione', clientMystere: 'Cliente Misterioso', clientMystereDesc: 'Valutate la qualità del servizio clienti attraverso le nostre visite misteriose.', visitesMysteres: 'Visite misteriose in punto vendita', appelsMysteres: 'Chiamate e contatti misteriosi', evaluationAccueil: "Valutazione dell'accoglienza", rapportRecommandations: 'Report dettagliato con raccomandazioni', suiviAmelioration: 'Monitoraggio dei piani di miglioramento', marketing: 'Marketing e Creazione Digitale', marketingDesc: 'Potenziate la vostra presenza online.', strategieDigital: 'Strategia di marketing digitale', creationSites: 'Creazione di siti web', reseauxSociaux: 'Gestione dei social network', creationContenu: 'Creazione di contenuti e design', campagnesPub: 'Campagne pubblicitarie online', expertise: 'La Nostra Esperienza', accelerate: 'Accelerate la Crescita della Vostra Azienda', expertiseText1: 'Excelsium Conseils accompagna le aziende nel loro sviluppo commerciale.', expertiseText2: 'Dalla strategia alla formazione, siamo il vostro partner di fiducia.', entreprises: 'Aziende Accompagnate', satisfaction: 'Soddisfazione del Cliente', secteurs: 'Settori di Attività', results: 'I Nostri Risultati', concreteResults: 'Risultati Concreti per la Vostra Azienda', resultsText: 'Approccio orientato ai risultati per valore misurabile.', methodology: 'Metodologia collaudata e adattabile', expertsConsultants: 'Consulenti esperti', personalizedSupport: 'Accompagnamento personalizzato', croissance: 'Crescita media del fatturato', auditsReussis: 'Audit superati', collaborateurs: 'Collaboratori formati', recommandation: 'Tasso di raccomandazione', multiSector: 'Esperienza Multisettoriale', readyToDevelop: 'Pronti a Sviluppare la Vostra Attività?', contactForAnalysis: "Contattateci per un'analisi delle vostre esigenze.", requestQuote: 'Richiedere un Preventivo', seeDetails: 'Vedi offerte' },
    cm: { tagline: 'Cliente Misterioso', heroTitle: 'Il Cliente Misterioso al Servizio della Vostra Eccellenza', heroSubtitle: 'Valutate oggettivamente la qualità della vostra accoglienza e del servizio clienti.', simpleProcess: 'Semplice ed Efficace', howItWorks: 'Come Funziona', step1Title: 'Preparazione', step1Desc: 'Personalizziamo insieme i parametri delle vostre visite misteriose.', step2Title: 'Visite Misteriose', step2Desc: "I nostri clienti misteriosi valutano ogni aspetto dell'esperienza.", step3Title: 'Risultati e Analisi', step3Desc: 'Ricevete un rapporto dettagliato dopo ogni visita misteriosa.', objectivesTag: 'Perché il Cliente Misterioso', objectivesTitle: 'Quattro Obiettivi Strategici', obj1Title: 'Aumentare il Fatturato', obj1Desc: "Il cliente misterioso permette di rivelare e migliorare le strategie commerciali.", obj2Title: 'Motivare i Team', obj2Desc: 'Un rapporto evidenzia più punti positivi che negativi.', obj3Title: 'Migliorare la Reputazione Online', obj3Desc: "Analisi dettagliata per identificare e correggere le disfunzioni.", obj4Title: 'Controllare gli Incassi', obj4Desc: 'Scenario specifico per mettere in sicurezza i processi di incasso.', packTag: 'Offerta Completa', packTitle: 'Il Pack Cliente Misterioso Tutto Incluso', packIntro: 'Offerta semplice, completa e senza sorprese.', packItem1: 'Personalizzazione dello studio', packItem2: 'Visite misteriose pianificate', packItem3: 'Rapporti dettagliati delle visite', packItem4: 'Opzionale: acquisto da parte del cliente misterioso', packItem5: 'Opzionale: acquisto da parte del cliente misterioso', guaranteeTitle: '', guaranteeDesc: '', guaranteeText: '', ctaTitle: 'Pronti a Migliorare il Vostro Servizio Clienti?', ctaText: 'Scoprite le nostre tariffe adattate a tutte le esigenze.', seePricing: 'Vedi Tariffe' },
    cmTarifs: { heroTitle: 'Tariffe Cliente Misterioso', heroSubtitle: 'Piani tutto incluso adattati al vostro volume di visite.', backToExplanation: 'Torna alle spiegazioni', pricingTag: 'I Nostri Piani', pricingTitle: 'Scegliete il Vostro Pack', allInclusive: 'Pack tutto incluso', eurPerVisit: '€ / visita', visit: 'visita', visitsMin: 'visite minimo', feat1: 'Questionario personalizzato', feat2: 'Visite in loco o telefoniche', feat3: 'Rapporto dettagliato per visita', feat4: 'Accompagnamento personalizzato', order: 'Ordinare', includedTag: 'Tutto Incluso', includedTitle: 'Cosa Include Ogni Pack', inc1: 'Personalizzazione completa dello studio', inc2: 'Pianificazione flessibile', inc3: 'Rapporti dettagliati dopo ogni visita', inc4: 'Accompagnamento personalizzato', guarantee: '', ctaTitle: 'Bisogno di un Preventivo Personalizzato?', ctaText: 'Per esigenze specifiche, contattateci direttamente.', contactUs: 'Contattaci', onlineDiscountTitle: '-30% su tutti gli interventi online', onlineDiscountDesc: 'Chiamate misteriose, valutazioni telefoniche e visite online: -30% rispetto alle tariffe in loco.' },
    conseils: { tagline: 'Consulenza', heroTitle: 'Consulenza Strategica per la Vostra Azienda', heroSubtitle: 'Accompagnamento su misura per sviluppare la vostra attività.', backToPro: 'Torna ai servizi professionali', seeOffer: "Vedi offerta dettagliata", ctaTitle: 'Bisogno di Consulenza Personalizzata?', ctaText: 'Contattateci per discutere le vostre esigenze.', contactUs: 'Contattaci' },
    formations: { tagline: 'Formazione', heroTitle: 'Formazione Professionale Certificata', heroSubtitle: 'Formazione tecnica e qualificante per lo sviluppo delle competenze.', backToPro: 'Torna ai servizi professionali', levels: 'Livelli', program: 'Programma', aiprTitle: 'AIPR - Reti', aiprDuration: '1 a 2 giorni', aiprDesc: 'Formazione per lavorare vicino alle reti.', aiprLevel1: 'Progettista', aiprLevel2: 'Supervisore', aiprLevel3: 'Operatore', aiprDetail1: 'Normative e obblighi legali', aiprDetail2: "Preparazione all'esame AIPR", elecTitle: 'Abilitazioni Elettriche BT', elecDuration: '1 a 3 giorni', elecDesc: 'Formazione in abilitazioni elettriche a bassa tensione.', elecLevel1: 'B0/H0/H0V', elecLevel2: 'BS/BE', elecLevel3: 'B1/B1V/B2/B2V', elecLevel4: 'BR/BC', elecDetail1: 'Conoscere i rischi elettrici', elecDetail2: 'Eseguire operazioni elettriche in sicurezza', elecDetail3: 'Interventi in bassa tensione', elecDetail4: 'Preparazione alla certificazione', telecomTitle: 'Telecom - Fibra Ottica', telecomDuration: '3 mesi', telecomDesc: 'Formazione completa sulle tecniche della fibra ottica.', telecomDetail1: 'Lettura e creazione di schemi', telecomDetail2: 'Posa dei cavi aerei, sotterranei, interni e in facciata', telecomDetail3: 'Giunzione per fusione', telecomDetail4: 'Misurazioni e test OTDR e fotometro', telecomDetail5: 'Connessione degli abbonati', telecomDetail6: 'Uso di attrezzature professionali', domotiqueTitle: 'Tecnico Edifici Comunicanti e Connessi', domotiqueDuration: '6 mesi', domotiqueDesc: 'Formazione in gestione tecnica degli edifici e domotica.', domotiqueDetail1: 'Gestione tecnica degli edifici (GTB) e gestione tecnica centralizzata (GTC)', domotiqueDetail2: 'Installazione di sistemi domotici', domotiqueDetail3: 'Automazione: tapparelle, cancelli, illuminazione intelligente', domotiqueDetail4: 'Sensori, allarme e videosorveglianza', domotiqueDetail5: 'Configurazione di oggetti connessi tramite protocollo KNX', domotiqueDetail6: 'Manutenzione preventiva e correttiva', ctaTitle: 'Interessati a una Formazione?', ctaText: 'Contattateci per le prossime sessioni e tariffe.', contactUs: 'Contattaci' },
    contact: { title: 'Contattaci', subtitle: 'Il nostro team è a vostra disposizione', info: 'Informazioni', stayInTouch: 'Restiamo in Contatto', infoText: 'Non esitate a contattarci per qualsiasi domanda.', phone: 'Telefono', whatsappNote: 'Se non siamo raggiungibili, inviate un messaggio via WhatsApp.', email: 'Email', address: 'Indirizzo', hours: 'Orari di Apertura', weekdays: 'Lunedì - Venerdì', saturday: 'Sabato', sunday: 'Domenica', byAppointment: 'Su appuntamento', closed: 'Chiuso', form: 'Modulo', sendMessage: 'Inviateci un Messaggio', firstName: 'Nome', lastName: 'Cognome', yourFirstName: 'Il vostro nome', yourLastName: 'Il vostro cognome', yourEmail: 'vostro@email.com', phonePlaceholder: '+33 6 12 34 56 78', service: 'Servizio interessato', selectService: 'Selezionate un servizio', message: 'Messaggio', messagePlaceholder: 'Descrivete il vostro progetto...', send: 'Inviare il Messaggio', sending: 'Invio in corso...', success: 'Messaggio Inviato!', successText: 'Grazie per averci contattato. Vi risponderemo al più presto.', sendAnother: 'Inviare un altro messaggio', services: { visaSchengen: 'Visto Schengen', visaCanada: 'Visto Canada', gestionImmo: 'Gestione Immobiliare', maitriseOeuvre: 'Direzione Lavori', strategie: 'Strategia Commerciale', formation: 'Formazione dei Team', hygiene: 'Igiene e Pulizia Industriale', clientMystere: 'Cliente Misterioso', marketing: 'Marketing Digitale', other: 'Altra richiesta' } },
    footer: { description: 'Il vostro partner di fiducia per le pratiche di visto e la consulenza aziendale.', particuliers: 'Privati', professionnels: 'Professionisti', contact: 'Contatto', visaSchengen: 'Visto Schengen', visaCanada: 'Visto Canada', accompagnement: 'Accompagnamento', gestionImmo: 'Gestione Immobiliare', maitriseOeuvre: 'Direzione Lavori', strategie: 'Strategia Commerciale', formations: 'Formazione', hygiene: 'Igiene e Pulizia Industriale', clientMystere: 'Cliente Misterioso', marketing: 'Marketing Digitale', rights: 'Tutti i diritti riservati.', legal: 'Note Legali', privacy: 'Politica sulla Privacy', taxNotice: 'Tutti i prezzi esposti sul sito sono al netto delle imposte (IVA esclusa). IVA applicabile: 19%.' }
  }
};

export default translations;
